<!-- ViewApplicationDetails.vue - Admin Application Detail Modal with Print & PDF Feature -->
<template>
  <BaseModal
    :is-visible="isVisible"
    :title="`Application Ref #${application?.application_id || ''}`"
    :subtitle="application ? `Submitted by ${application.full_name}` : 'Loading application details...'"
    :icon="FileText"
    size="4xl"
    type="default"
    :show-footer="true"
    @close="closeModal"
  >
    <div v-if="!application" class="p-8 text-center text-gray-500">
      <vue-loaders name="line-scale" color="#035fa3" scale="1.2" class="mb-3" />
      <p>Loading application details...</p>
    </div>

    <div v-else class="space-y-6">
      <!-- Top Applicant Hero Summary Banner -->
      <div class="p-5 rounded-2xl bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/60 dark:to-gray-800/60 border border-gray-200 dark:border-gray-700 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div class="flex items-start sm:items-center space-x-4">
          <!-- Avatar Icon -->
          <div class="w-14 h-14 rounded-2xl bg-secondary text-white flex items-center justify-center text-xl font-black shadow-md flex-shrink-0">
            {{ getInitials(application.full_name) }}
          </div>
          <div>
            <div class="flex flex-wrap items-center gap-x-2 gap-y-1.5">
              <h2 class="text-xl font-bold text-gray-900 dark:text-white">
                {{ application.full_name }}
              </h2>
              <span
                class="px-2.5 py-0.5 rounded-full text-xs font-bold"
                :class="getStatusBadgeClass(application.application_status)"
              >
                {{ formatStatus(application.application_status) }}
              </span>

              <!-- Paper Record Badge -->
              <span
                v-if="application.record_type === 'paper'"
                class="px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-800 dark:bg-purple-900/60 dark:text-purple-300 inline-flex items-center gap-1.5"
              >
                <FileText class="w-3.5 h-3.5 text-purple-700 dark:text-purple-300" />
                <span>Paper Record (Digitized)</span>
              </span>

              <!-- Linked Staff Badge (Paper Record linked to existing staff) -->
              <span
                v-if="application.fk_linked_user_id && application.fk_linked_user_id !== application.fk_created_user_id"
                class="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-indigo-100 text-indigo-800 dark:bg-indigo-900/60 dark:text-indigo-300 flex items-center space-x-1"
                :title="`Linked to Staff Member User #${application.fk_linked_user_id}`"
              >
                <Users class="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                <span>Linked Staff #{{ application.fk_linked_user_id }}</span>
              </span>

              <!-- Staff Account Provisioning Status Badge -->
              <span
                v-if="application.fk_created_user_id || (application.fk_linked_user_id && application.fk_linked_user_id === application.fk_created_user_id)"
                class="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300 flex items-center space-x-1"
              >
                <CheckCircle2 class="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                <span>Staff Account Active (#{{ application.fk_created_user_id || application.fk_linked_user_id }})</span>
              </span>
              <span
                v-else-if="canCreateStaffAccount(application)"
                class="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300 flex items-center space-x-1"
              >
                <Clock class="w-3 h-3 text-amber-600 dark:text-amber-400" />
                <span>Staff Account Pending</span>
              </span>

              <!-- Edited by Applicant Badge -->
              <span
                v-if="application.applicant_edit_count && application.applicant_edit_count > 0"
                class="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300 inline-flex items-center space-x-1"
                :title="`Edited by applicant ${application.applicant_edit_count} time(s). Last edit: ${formatDate(application.last_edited_by_applicant_at)}`"
              >
                <History class="w-3 h-3 text-blue-600 dark:text-blue-400" />
                <span>Edited by Applicant ({{ application.applicant_edit_count }}x)</span>
              </span>
            </div>
            <p class="text-sm text-gray-600 dark:text-gray-300 mt-0.5">
              <span class="font-semibold text-secondary dark:text-secondary-dark-text">{{ application.position_applied_for || 'Position Not Specified' }}</span>
              • Submitted on {{ formatDate(application.submitted_at || application.created_at) }}
            </p>
          </div>
        </div>

        <!-- Quick Action Buttons -->
        <div class="flex shrink-0 items-center space-x-2 self-end md:self-center flex-wrap gap-y-2">
          <!-- Resend Applicant Edit Link Button (online applications only) -->
          <BaseButton
            v-if="application.record_type !== 'paper' && application.is_editable_by_applicant"
            variant="outline"
            size="md"
            text="Resend Edit Link"
            :icon="Send"
            icon-position="left"
            :loading="isResendingLink"
            class="whitespace-nowrap"
            @click="handleResendAccessLink"
          />

          <!-- Revoke Access Button (if active tokens exist) -->
          <BaseButton
            v-if="application.record_type !== 'paper' && application.has_active_access_link"
            variant="outline"
            size="md"
            text="Revoke Access"
            :icon="KeyRound"
            icon-position="left"
            :loading="isRevokingAccess"
            class="whitespace-nowrap text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 dark:hover:bg-red-950/40"
            @click="handleRevokeAccess"
          />

          <!-- Create Staff Account Button (if Approved or Paper record and no account yet) -->
          <BaseButton
            v-if="canCreateStaffAccount(application)"
            variant="success"
            size="md"
            text="Create Staff"
            :icon="UserPlus"
            icon-position="left"
            class="whitespace-nowrap"
            @click="$emit('create-account', application)"
          />

          <!-- Delete Application Button -->
          <BaseButton
            variant="outline"
            size="md"
            text="Delete"
            :icon="Trash2"
            icon-position="left"
            class="whitespace-nowrap text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 dark:hover:bg-red-950/40"
            @click="$emit('delete', application)"
          />
        </div>
      </div>

      <!-- Navigation Tabs (Responsive Segmented Grid - No Horizontal Scrollbar) -->
      <div
        class="p-1.5 rounded-2xl bg-gray-100 dark:bg-gray-800/90 border border-gray-200/80 dark:border-gray-700/80 grid gap-1.5 shadow-inner"
        :class="isPaperRecord ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-6'"
      >
        <button
          v-for="tab in visibleTabs"
          :key="tab.id"
          type="button"
          class="w-full px-2 py-2 sm:py-2.5 text-xs font-bold rounded-xl transition-all duration-200 flex items-center justify-center space-x-1.5 text-center truncate select-none"
          :class="[
            activeTab === tab.id
              ? 'bg-white dark:bg-gray-700 text-secondary dark:text-secondary-dark-text shadow-sm ring-1 ring-black/5 dark:ring-white/10'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-white/50 dark:hover:bg-gray-700/50'
          ]"
          @click="activeTab = tab.id"
        >
          <component :is="tab.icon" class="w-3.5 h-3.5 flex-shrink-0" />
          <span class="truncate">{{ tab.label }}</span>
          <span
            v-if="tab.id === 'documents' && localDocuments.length"
            class="px-1.5 py-0.2 rounded-full text-[10px] bg-secondary/10 text-secondary dark:text-secondary-dark-text font-bold"
          >
            {{ localDocuments.length }}
          </span>
          <span
            v-else-if="tab.id === 'print_pdf'"
            class="px-1 py-0.2 rounded text-[9px] bg-secondary/10 text-secondary dark:text-secondary-dark-text font-extrabold uppercase"
          >
            PDF
          </span>
        </button>
      </div>

      <!-- TAB 1: EMPLOYMENT APPLICATION FORM -->
      <div v-show="activeTab === 'job_application'" class="space-y-6">
        <!-- ==================================================== -->
        <!-- PAPER APPLICATION VIEW (record_type === 'paper')     -->
        <!-- ==================================================== -->
        <template v-if="isPaperRecord">
          <!-- Digitized Paper Record Notice Banner -->
          <div
            class="p-4 rounded-2xl bg-purple-50 dark:bg-purple-950/30 border border-purple-200 dark:border-purple-800/50 flex items-start space-x-3"
          >
            <div class="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-700 dark:text-purple-300 flex-shrink-0">
              <FileText class="w-5 h-5" />
            </div>
            <div>
              <h4 class="text-sm font-bold text-purple-900 dark:text-purple-200">
                Digitized Paper Record
              </h4>
              <p class="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
                This record was digitized from an offline/paper application. Original scanned documents and physical paperwork attachments can be viewed and downloaded under the <button type="button" class="underline font-bold hover:text-purple-900 dark:hover:text-purple-100" @click="activeTab = 'documents'">Attachments</button> tab.
              </p>
            </div>
          </div>

          <!-- Section 1: Applicant Information -->
          <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
              <User class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>1. Applicant Information</span>
            </h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Full Legal Name</span>
                <p class="font-bold text-gray-900 dark:text-white">{{ application.full_name }}</p>
              </div>
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Email Address</span>
                <p class="font-bold text-gray-900 dark:text-white break-all">
                  {{ isPlaceholderEmail(application.email) ? 'Not provided (Digitized Paper Record)' : (application.email || 'Not provided') }}
                </p>
              </div>
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Phone Number</span>
                <p class="font-bold text-gray-900 dark:text-white">{{ application.phone_number || 'Not provided' }}</p>
              </div>
              <div class="sm:col-span-2">
                <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Street / Residential Address</span>
                <p class="font-bold text-gray-900 dark:text-white">
                  {{ formatAddress(application) || 'Not provided' }}
                </p>
              </div>
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Record Type</span>
                <p class="font-bold text-purple-700 dark:text-purple-300 flex items-center space-x-1.5">
                  <FileText class="w-3.5 h-3.5" />
                  <span>Offline / Paper Record</span>
                </p>
              </div>
            </div>
          </div>

          <!-- Section 2: Position & Availability -->
          <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
              <Briefcase class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>2. Position & Availability</span>
            </h3>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Position Applied / Hired For</span>
                <p class="font-bold text-gray-900 dark:text-white">{{ application.position_applied_for || 'Not specified' }}</p>
              </div>
              <div>
                <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Availability / Start Date</span>
                <p class="font-bold text-gray-900 dark:text-white">{{ application.availability_date ? formatDate(application.availability_date) : 'Not specified' }}</p>
              </div>
            </div>
          </div>

          <!-- Section 3: Previous Experience Summary -->
          <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-3">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
              <History class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>3. Previous Experience Summary</span>
            </h3>

            <div v-if="application.previous_experience" class="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
              <p class="text-xs sm:text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                {{ application.previous_experience }}
              </p>
            </div>
            <div v-else class="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 text-xs text-gray-400 italic">
              No previous experience summary was entered for this paper record.
            </div>
          </div>

          <!-- Section 4: Administrator Remarks & Archival Notes -->
          <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-3">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
              <ClipboardCheck class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>4. Administrator Remarks & Notes</span>
            </h3>

            <div v-if="application.admin_remarks" class="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
              <p class="text-xs sm:text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed">
                {{ application.admin_remarks }}
              </p>
            </div>
            <div v-else class="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 text-xs text-gray-400 italic">
              No administrative remarks logged.
            </div>
          </div>

          <!-- Section 5: Scanned Documents Overview -->
          <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
                <Paperclip class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
                <span>5. Scanned Documents & Attachments ({{ localDocuments.length }})</span>
              </h3>
              <button
                type="button"
                class="text-xs text-secondary dark:text-secondary-dark-text font-bold hover:underline flex items-center space-x-1"
                @click="activeTab = 'documents'"
              >
                <span>Manage Attachments</span>
                <span>&rarr;</span>
              </button>
            </div>

            <div v-if="!localDocuments.length" class="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 text-xs text-gray-400 italic">
              No physical documents were attached to this record yet. You can upload scanned copies in the <button type="button" class="underline font-bold text-secondary dark:text-secondary-dark-text" @click="activeTab = 'documents'">Attachments tab</button>.
            </div>
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                v-for="doc in localDocuments"
                :key="doc.document_id"
                class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 flex items-center justify-between space-x-2"
              >
                <div class="flex items-center space-x-2.5 min-w-0 flex-1">
                  <div class="p-2 rounded-lg bg-secondary/10 text-secondary dark:text-secondary-dark-text flex-shrink-0">
                    <FileText class="w-4 h-4" />
                  </div>
                  <div class="min-w-0 flex-1">
                    <p class="font-bold text-xs text-gray-900 dark:text-white truncate">
                      {{ doc.file_name }}
                    </p>
                    <p class="text-[10px] text-gray-500 capitalize">
                      {{ doc.document_category?.replace(/_/g, ' ') || 'paper record' }} • {{ formatFileSize(doc.file_size) }}
                    </p>
                  </div>
                </div>
                <div class="flex items-center space-x-1 flex-shrink-0">
                  <button
                    type="button"
                    class="p-1.5 rounded-lg text-secondary dark:text-secondary-dark-text hover:bg-secondary/10 transition"
                    title="Preview Document"
                    @click="handlePreview(doc)"
                  >
                    <Eye class="w-3.5 h-3.5" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                    title="Download Document"
                    @click="handleDownload(doc)"
                  >
                    <Download class="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- ==================================================== -->
        <!-- ONLINE APPLICATION VIEW (record_type !== 'paper')    -->
        <!-- ==================================================== -->
        <template v-else>

        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <User class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>1. Applicant Information</span>
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Full Legal Name</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ application.full_name }}</p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Email Address</span>
              <p class="font-bold text-gray-900 dark:text-white break-all">{{ application.email }}</p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Phone Number</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ application.phone_number || 'N/A' }}</p>
            </div>
            <div class="sm:col-span-2">
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Street Address</span>
              <p class="font-bold text-gray-900 dark:text-white">
                {{ application.street_address }}, {{ application.city }}, {{ application.state }} {{ application.zip_code }}
              </p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Date of Birth</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ jobApp?.date_of_birth || 'N/A' }}</p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Social Security Number</span>
              <p class="font-bold text-gray-900 dark:text-white flex items-center space-x-2">
                <span>{{ isSsnRevealed ? (formatFullSsn(applicantSsn) || 'N/A') : maskSsn(applicantSsn) }}</span>
                <button
                  type="button"
                  class="text-xs text-secondary dark:text-secondary-dark-text hover:underline"
                  @click="isSsnRevealed = !isSsnRevealed"
                >
                  {{ isSsnRevealed ? 'Hide' : 'Reveal' }}
                </button>
              </p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">NPI Number</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ jobApp?.npi_number || 'None' }}</p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Work Authorization Eligibility</span>
              <p class="font-bold text-gray-900 dark:text-white">
                {{ jobApp?.is_legally_eligible_to_work ? 'Yes (Authorized to work in US)' : 'No' }}
              </p>
            </div>
          </div>
        </div>

        <!-- 2. Position & Availability -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <Clock class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>2. Position & Availability</span>
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Position Applied</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ application.position_applied_for || 'N/A' }}</p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Employment Desired</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ jobApp?.employment_desired || 'N/A' }}</p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Weekly Hours Available</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ jobApp?.weekly_hours_available || '40' }} hrs/wk</p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Available Start Date</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ application.availability_date || jobApp?.date_available_for_work || 'Immediately' }}</p>
            </div>
          </div>

          <!-- Schedule availability days -->
          <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 space-y-2">
            <div class="flex items-center justify-between text-xs">
              <span class="font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Days & Hours Available to Work</span>
              <span v-if="jobApp?.availability_no_pref" class="text-xs font-semibold text-secondary dark:text-secondary-dark-text bg-secondary/10 px-2 py-0.5 rounded-full">
                ✓ Open Availability (No Preference)
              </span>
            </div>
            <div class="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2 text-xs">
              <div class="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                <span class="text-[10px] text-gray-400 font-bold block">MON</span>
                <span class="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{{ jobApp?.available_mon || '—' }}</span>
              </div>
              <div class="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                <span class="text-[10px] text-gray-400 font-bold block">TUE</span>
                <span class="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{{ jobApp?.available_tue || '—' }}</span>
              </div>
              <div class="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                <span class="text-[10px] text-gray-400 font-bold block">WED</span>
                <span class="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{{ jobApp?.available_wed || '—' }}</span>
              </div>
              <div class="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                <span class="text-[10px] text-gray-400 font-bold block">THU</span>
                <span class="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{{ jobApp?.available_thur || '—' }}</span>
              </div>
              <div class="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                <span class="text-[10px] text-gray-400 font-bold block">FRI</span>
                <span class="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{{ jobApp?.available_fri || '—' }}</span>
              </div>
              <div class="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                <span class="text-[10px] text-gray-400 font-bold block">SAT</span>
                <span class="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{{ jobApp?.available_sat || '—' }}</span>
              </div>
              <div class="p-2 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-center">
                <span class="text-[10px] text-gray-400 font-bold block">SUN</span>
                <span class="font-semibold text-gray-800 dark:text-gray-200 text-[11px]">{{ jobApp?.available_sun || '—' }}</span>
              </div>
            </div>
            <div class="text-xs text-gray-500 flex items-center gap-4 pt-1">
              <span>Can work nights: <strong class="text-gray-800 dark:text-gray-200">{{ jobApp?.can_work_nights || 'N/A' }}</strong></span>
            </div>
          </div>
        </div>

        <!-- 3. Education History -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-3">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <GraduationCap class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>3. Education History</span>
          </h3>

          <div class="overflow-x-auto">
            <table class="w-full text-xs text-left text-gray-700 dark:text-gray-300">
              <thead class="text-[11px] uppercase bg-gray-100 dark:bg-gray-900/50 text-gray-600 dark:text-gray-400">
                <tr>
                  <th class="p-2.5">Level</th>
                  <th class="p-2.5">School Name</th>
                  <th class="p-2.5">Location</th>
                  <th class="p-2.5">Years Completed</th>
                  <th class="p-2.5">Major / Degree</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-100 dark:divide-gray-800">
                <tr v-for="(edu, idx) in jobApp?.education_history || []" :key="idx">
                  <td class="p-2.5 font-bold">{{ edu.type }}</td>
                  <td class="p-2.5">{{ edu.name_of_school || '—' }}</td>
                  <td class="p-2.5">{{ edu.location || '—' }}</td>
                  <td class="p-2.5">{{ edu.years_completed || '—' }}</td>
                  <td class="p-2.5">{{ edu.major_degree || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 4. Background & Transportation -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <Car class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>4. Background & Transportation</span>
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Convicted of Crime</span>
              <p class="font-bold" :class="jobApp?.has_been_convicted_of_crime ? 'text-red-500' : 'text-gray-900 dark:text-white'">
                {{ jobApp?.has_been_convicted_of_crime ? 'Yes' : 'No' }}
              </p>
              <p v-if="jobApp?.has_been_convicted_of_crime" class="text-xs text-red-600 dark:text-red-400 mt-1">
                {{ jobApp?.crime_conviction_explanation }}
              </p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Driver's License</span>
              <p class="font-bold text-gray-900 dark:text-white">
                {{ jobApp?.has_driver_license ? `Yes (${jobApp?.driver_license_state} #${jobApp?.driver_license_number || 'N/A'})` : 'No' }}
              </p>
            </div>
            <div>
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Transportation Means</span>
              <p class="font-bold text-gray-900 dark:text-white">{{ jobApp?.transportation_means || 'Personal Vehicle' }}</p>
            </div>
          </div>
        </div>

        <!-- 5. Computer & Administrative Skills -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-3">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <Cpu class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>5. Computer & Administrative Skills</span>
          </h3>

          <div class="flex flex-wrap gap-2 text-xs">
            <span v-if="jobApp?.skill_windows" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ Windows OS</span>
            <span v-if="jobApp?.skill_word" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ MS Word</span>
            <span v-if="jobApp?.skill_excel" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ MS Excel</span>
            <span v-if="jobApp?.skill_powerpoint" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ PowerPoint</span>
            <span v-if="jobApp?.skill_access" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ MS Access</span>
            <span v-if="jobApp?.skill_publisher" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ MS Publisher</span>
            <span v-if="jobApp?.skill_typing" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ Fast Typing</span>
            <span v-if="jobApp?.skill_wordperfect" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ WordPerfect</span>
            <span v-if="jobApp?.skill_pc" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ PC Architecture</span>
            <span v-if="jobApp?.skill_mac" class="px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-800">✓ Mac OS</span>
          </div>

          <div v-if="jobApp?.other_skills" class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 text-xs">
            <span class="font-bold text-gray-700 dark:text-gray-300">Other Skills & Certifications:</span>
            <p class="text-gray-600 dark:text-gray-400 mt-0.5">{{ jobApp.other_skills }}</p>
          </div>
        </div>

        <!-- 6. Work Experience History -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <Briefcase class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>6. Previous Work Experiences</span>
          </h3>

          <div v-if="!jobApp?.work_experiences?.length" class="text-xs text-gray-400 italic">
            No work experiences submitted.
          </div>
          <div v-else class="space-y-4">
            <div
              v-for="(work, idx) in jobApp.work_experiences"
              :key="idx"
              class="p-4 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 space-y-2 text-xs"
            >
              <div class="flex items-center justify-between">
                <span class="font-bold text-sm text-gray-900 dark:text-white">
                  {{ work.name_of_employer || `Employer #${idx + 1}` }}
                </span>
                <span class="text-gray-500">{{ work.from_date }} - {{ work.to_date || 'Present' }}</span>
              </div>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-2 text-gray-600 dark:text-gray-300">
                <p><span class="font-semibold text-gray-700 dark:text-gray-200">Title:</span> {{ work.last_job_title || 'N/A' }}</p>
                <p><span class="font-semibold text-gray-700 dark:text-gray-200">Supervisor:</span> {{ work.last_supervisor || 'N/A' }}</p>
                <p><span class="font-semibold text-gray-700 dark:text-gray-200">Phone:</span> {{ work.phone_number || 'N/A' }}</p>
              </div>
              <p v-if="work.duties_skills_promotions" class="text-gray-600 dark:text-gray-400">
                <span class="font-semibold text-gray-700 dark:text-gray-200">Duties / Skills:</span> {{ work.duties_skills_promotions }}
              </p>
            </div>
          </div>
        </div>

        <!-- 7. Employment References -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <div class="flex items-center justify-between flex-wrap gap-2">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
              <Users class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>7. Employment References (3 References)</span>
            </h3>

            <!-- Verification Summary Badge -->
            <div v-if="displayReferences?.length" class="flex items-center space-x-2">
              <span
                class="px-2.5 py-0.5 rounded-full text-xs font-bold inline-flex items-center space-x-1 border shadow-2xs"
                :class="
                  verifiedReferencesCount === displayReferences.length
                    ? 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-800'
                    : verifiedReferencesCount > 0
                    ? 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/60 dark:text-blue-300 dark:border-blue-800'
                    : 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-950/60 dark:text-amber-300 dark:border-amber-800'
                "
              >
                <CheckCircle2 v-if="verifiedReferencesCount === displayReferences.length" class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                <Clock v-else class="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                <span>{{ verifiedReferencesCount }} of {{ displayReferences.length }} Verified</span>
              </span>
            </div>
          </div>

          <div v-if="!displayReferences?.length" class="text-xs text-gray-400 italic">
            No employment references submitted.
          </div>
          <div v-else class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div
              v-for="(refItem, idx) in displayReferences"
              :key="idx"
              class="p-4 rounded-xl border flex flex-col justify-between space-y-3 text-xs transition-all duration-200"
              :class="
                refItem.is_verified
                  ? 'bg-emerald-50/40 dark:bg-emerald-950/20 border-emerald-300/80 dark:border-emerald-800/60 shadow-xs'
                  : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-800'
              "
            >
              <!-- Card Header: Title & Verification Badge -->
              <div class="space-y-1.5">
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0 flex-1">
                    <span class="text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                      Reference #{{ idx + 1 }}
                    </span>
                    <h4 class="font-bold text-sm text-gray-900 dark:text-white truncate" :title="refItem.name">
                      {{ refItem.name || `Reference #${idx + 1}` }}
                    </h4>
                  </div>

                  <!-- Verification Status Badge -->
                  <span
                    class="px-2 py-0.5 rounded-full text-[10px] font-bold inline-flex items-center space-x-1 shrink-0 border"
                    :class="
                      refItem.is_verified
                        ? 'bg-emerald-100 text-emerald-800 border-emerald-300 dark:bg-emerald-900/70 dark:text-emerald-200 dark:border-emerald-700'
                        : 'bg-amber-100 text-amber-800 border-amber-300 dark:bg-amber-900/60 dark:text-amber-200 dark:border-amber-700'
                    "
                  >
                    <CheckCircle2 v-if="refItem.is_verified" class="w-3 h-3 text-emerald-600 dark:text-emerald-300" />
                    <Clock v-else class="w-3 h-3 text-amber-600 dark:text-amber-300" />
                    <span>{{ refItem.is_verified ? 'Verified' : 'Pending' }}</span>
                  </span>
                </div>

                <p class="text-gray-600 dark:text-gray-300 font-medium">
                  {{ refItem.position_title || 'Position N/A' }} • {{ refItem.organization || 'Org N/A' }}
                </p>
              </div>

              <!-- Contact Information -->
              <div class="pt-2 border-t border-gray-200/60 dark:border-gray-800 space-y-1 text-gray-600 dark:text-gray-400">
                <p class="flex items-center space-x-1.5 truncate">
                  <Phone class="w-3 h-3 text-gray-400 shrink-0" />
                  <span>Phone:</span>
                  <a
                    v-if="refItem.phone_number"
                    :href="`tel:${refItem.phone_number}`"
                    class="font-semibold text-gray-900 dark:text-gray-100 hover:text-secondary dark:hover:text-secondary-dark-text hover:underline"
                  >
                    {{ refItem.phone_number }}
                  </a>
                  <span v-else class="text-gray-400">N/A</span>
                </p>
                <p class="flex items-center space-x-1.5 truncate">
                  <Mail class="w-3 h-3 text-gray-400 shrink-0" />
                  <span>Email:</span>
                  <a
                    v-if="refItem.email"
                    :href="`mailto:${refItem.email}`"
                    class="font-semibold text-gray-900 dark:text-gray-100 hover:text-secondary dark:hover:text-secondary-dark-text hover:underline truncate"
                  >
                    {{ refItem.email }}
                  </a>
                  <span v-else class="text-gray-400">N/A</span>
                </p>
              </div>

              <!-- Verified By Metadata Info -->
              <div
                v-if="refItem.is_verified"
                class="p-2 rounded-lg bg-emerald-100/70 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800/60 text-[11px] text-emerald-900 dark:text-emerald-200 flex items-start space-x-1.5"
              >
                <ShieldCheck class="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5" />
                <div class="min-w-0">
                  <p class="font-semibold leading-tight">Verified by {{ refItem.verified_by || 'Admin' }}</p>
                  <p v-if="refItem.verified_at" class="text-[10px] text-emerald-700 dark:text-emerald-300 mt-0.5">
                    {{ formatDate(refItem.verified_at) }}
                  </p>
                </div>
              </div>

              <!-- Verification Notes Display (when not currently editing this card) -->
              <div
                v-if="refItem.verification_notes && editingNoteIndex !== idx"
                class="p-2 rounded-lg bg-gray-100/80 dark:bg-gray-800/80 border border-gray-200/80 dark:border-gray-700/80 text-[11px] text-gray-700 dark:text-gray-300 space-y-0.5"
              >
                <div class="flex items-center justify-between text-gray-500 dark:text-gray-400 text-[10px] font-semibold uppercase">
                  <span class="flex items-center space-x-1">
                    <MessageSquare class="w-3 h-3" />
                    <span>Admin Notes</span>
                  </span>
                  <button
                    type="button"
                    class="hover:text-secondary dark:hover:text-secondary-dark-text underline cursor-pointer"
                    @click="openEditNotes(idx)"
                  >
                    Edit
                  </button>
                </div>
                <p class="whitespace-pre-wrap text-gray-800 dark:text-gray-200 leading-snug">
                  {{ refItem.verification_notes }}
                </p>
              </div>

              <!-- Inline Notes Editor (when active for this card) -->
              <div
                v-if="editingNoteIndex === idx"
                class="p-2.5 rounded-xl bg-white dark:bg-gray-800 border border-secondary/40 dark:border-secondary-dark/60 shadow-xs space-y-2"
              >
                <div class="flex items-center justify-between">
                  <span class="text-[11px] font-bold text-gray-800 dark:text-gray-200 flex items-center space-x-1">
                    <MessageSquare class="w-3 h-3 text-secondary dark:text-secondary-dark-text" />
                    <span>Verification Notes</span>
                  </span>
                  <button
                    type="button"
                    class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 cursor-pointer"
                    @click="cancelEditNotes"
                  >
                    <X class="w-3.5 h-3.5" />
                  </button>
                </div>
                <textarea
                  v-model="editingNoteText"
                  rows="2"
                  maxlength="1000"
                  class="w-full text-xs p-2 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-900/60 dark:text-white focus:ring-1 focus:ring-secondary focus:border-secondary outline-none resize-none"
                  placeholder="Record conversation details, feedback, or comments..."
                ></textarea>
                <div class="flex items-center justify-end space-x-1.5">
                  <button
                    type="button"
                    class="px-2 py-1 rounded text-[11px] font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                    @click="cancelEditNotes"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class="px-2.5 py-1 rounded text-[11px] font-bold bg-secondary hover:bg-secondary-dark text-white flex items-center space-x-1 cursor-pointer"
                    :disabled="savingIndex === idx"
                    @click="saveNotesOnly(idx)"
                  >
                    <Loader2 v-if="savingIndex === idx" class="w-3 h-3 animate-spin" />
                    <span>Save Note</span>
                  </button>
                </div>
              </div>

              <!-- Card Action Buttons -->
              <div class="pt-2 border-t border-gray-200/60 dark:border-gray-800 flex items-center justify-between gap-2 flex-wrap">
                <!-- Toggle Verification Button -->
                <button
                  type="button"
                  class="flex-1 py-1.5 px-2.5 rounded-lg text-xs font-bold transition-colors inline-flex items-center justify-center space-x-1.5 cursor-pointer shadow-2xs"
                  :class="
                    refItem.is_verified
                      ? 'bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700 hover:border-red-200 border border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700 dark:hover:bg-red-950/40 dark:hover:text-red-300'
                      : 'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500'
                  "
                  :disabled="savingIndex === idx"
                  @click="toggleReferenceVerification(idx)"
                >
                  <Loader2 v-if="savingIndex === idx" class="w-3.5 h-3.5 animate-spin" />
                  <template v-else-if="refItem.is_verified">
                    <X class="w-3.5 h-3.5 text-gray-500 hover:text-red-600" />
                    <span>Unmark Verified</span>
                  </template>
                  <template v-else>
                    <Check class="w-3.5 h-3.5" />
                    <span>Verify Reference</span>
                  </template>
                </button>

                <!-- Add/Edit Note Button (if not already opened) -->
                <button
                  v-if="editingNoteIndex !== idx"
                  type="button"
                  class="py-1.5 px-2 rounded-lg text-xs font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-200/60 dark:hover:bg-gray-800 border border-gray-200 dark:border-gray-700 inline-flex items-center space-x-1 cursor-pointer"
                  :title="refItem.verification_notes ? 'Edit note' : 'Add note'"
                  @click="openEditNotes(idx)"
                >
                  <Edit3 v-if="refItem.verification_notes" class="w-3 h-3" />
                  <MessageSquare v-else class="w-3 h-3" />
                  <span>{{ refItem.verification_notes ? 'Edit' : 'Note' }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- 8. Signatures & Waivers -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <CheckCircle2 class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>8. Signatures & Waivers</span>
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
            <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 space-y-1">
              <span class="text-gray-500 font-medium">Application Form Waiver Signature</span>
              <p class="font-bold text-sm text-gray-900 dark:text-white font-mono">{{ jobApp?.waiver_signature || 'N/A' }}</p>
              <p class="text-[11px] text-gray-400">Date: {{ jobApp?.waiver_date }}</p>
            </div>
            <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800 space-y-1">
              <span class="text-gray-500 font-medium">Job Description Acknowledgment Signature</span>
              <p class="font-bold text-sm text-gray-900 dark:text-white font-mono">{{ jobApp?.job_description_signature || 'N/A' }}</p>
              <p class="text-[11px] text-gray-400">Date: {{ jobApp?.job_description_date }}</p>
            </div>
          </div>
        </div>
        </template>
      </div>

      <!-- TAB 2: BACKGROUND CHECK -->
      <div v-if="!isPaperRecord" v-show="activeTab === 'background_check'" class="space-y-4">
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
            <ShieldCheck class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
            <span>Background Check Requirements & Acknowledgment</span>
          </h3>

          <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800">
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Requirements Acknowledged</span>
              <p class="font-bold mt-1" :class="application.background_check_data?.acknowledges_requirements ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'">
                {{ application.background_check_data?.acknowledges_requirements ? '✓ Yes, Confirmed' : '✗ Not Acknowledged' }}
              </p>
            </div>

            <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800">
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Full Legal Name Signed</span>
              <p class="font-bold text-gray-900 dark:text-white mt-1">
                {{ application.background_check_data?.applicant_name || application.full_name }}
              </p>
            </div>

            <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-100 dark:border-gray-800">
              <span class="text-xs text-gray-500 dark:text-gray-400 font-medium">Signed Date</span>
              <p class="font-bold text-gray-900 dark:text-white mt-1">
                {{ application.background_check_data?.acknowledged_at || formatDate(application.submitted_at) }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 3: TAX DOCUMENTATION -->
      <div v-if="!isPaperRecord" v-show="activeTab === 'tax_docs'" class="space-y-6">
        <!-- Form MW507 -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4 shadow-sm">
          <div class="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-3">
            <div>
              <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
                <Receipt class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
                <span>1. State of Maryland Form MW507</span>
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">Employee's Maryland Withholding Exemption Certificate</p>
            </div>
            <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-secondary/10 text-secondary dark:text-secondary-dark-text">
              State Tax
            </span>
          </div>

          <!-- MW507 Personal Info -->
          <div class="space-y-2">
            <h4 class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Personal & Filing Information</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Full Name on MW507</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.mw507?.full_name || application.full_name }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Social Security Number</span>
                <p class="font-bold text-gray-900 dark:text-white font-mono mt-0.5">
                  {{ isSsnRevealed ? (formatFullSsn(taxDocs?.mw507?.social_security_number || applicantSsn) || 'N/A') : maskSsn(taxDocs?.mw507?.social_security_number || applicantSsn) }}
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Maryland Filing Status</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.mw507?.filing_status || 'Single' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 sm:col-span-2">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Residential Street Address</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ taxDocs?.mw507?.street_address || 'N/A' }}, {{ taxDocs?.mw507?.city || '' }} {{ taxDocs?.mw507?.state || 'MD' }} {{ taxDocs?.mw507?.zip_code || '' }}
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">County of Residence / Employment</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.mw507?.county_of_residence || 'N/A' }}</p>
              </div>
            </div>
          </div>

          <!-- MW507 Allowances & Special Exemptions -->
          <div class="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <h4 class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Withholding Allowances & Special Exemptions</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Line 1: Total Exemptions Claimed</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5 text-sm">{{ taxDocs?.mw507?.line1_total_exemptions ?? '1' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Line 2: Additional Withholding</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5 text-sm">${{ taxDocs?.mw507?.line2_additional_withholding || '0' }} / pay period</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Line 3: Exemption from Tax</span>
                <p class="font-bold mt-0.5" :class="(taxDocs?.mw507?.line3_a_last_year_no_tax || taxDocs?.mw507?.line3_b_this_year_no_tax) ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'">
                  <span v-if="taxDocs?.mw507?.line3_a_last_year_no_tax && taxDocs?.mw507?.line3_b_this_year_no_tax">Full Exemption (3a & 3b)</span>
                  <span v-else-if="taxDocs?.mw507?.line3_a_last_year_no_tax">Claimed (Last year no tax)</span>
                  <span v-else-if="taxDocs?.mw507?.line3_b_this_year_no_tax">Claimed (This year no tax)</span>
                  <span v-else>No Exemption Claimed</span>
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Line 4 / Line 8 Reciprocity State</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ taxDocs?.mw507?.line4_domiciled_state || taxDocs?.mw507?.line8_military_spouse_state || 'None (MD Resident)' }}
                </p>
              </div>
            </div>
          </div>

          <!-- MW507 Signature -->
          <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
            <div>
              <span class="text-gray-500 dark:text-gray-400">MW507 Employee Digital Signature:</span>
              <strong class="text-gray-900 dark:text-white font-mono ml-1.5 text-sm">{{ taxDocs?.mw507?.employee_signature || application.full_name }}</strong>
            </div>
            <div class="text-gray-500 dark:text-gray-400">
              Date Signed: <strong class="text-gray-800 dark:text-gray-200">{{ taxDocs?.mw507?.signature_date || 'N/A' }}</strong>
            </div>
          </div>
        </div>

        <!-- Form I-9 -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4 shadow-sm">
          <div class="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-3">
            <div>
              <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
                <ShieldCheck class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
                <span>2. USCIS Form I-9 (Section 1)</span>
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">Employment Eligibility Verification — Department of Homeland Security</p>
            </div>
            <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-secondary/10 text-secondary dark:text-secondary-dark-text">
              Federal Verification
            </span>
          </div>

          <!-- I-9 Employee Info -->
          <div class="space-y-2">
            <h4 class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Employee Information</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Employee Name</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ taxDocs?.i9?.first_name || '' }} {{ taxDocs?.i9?.middle_initial ? taxDocs?.i9?.middle_initial + '.' : '' }} {{ taxDocs?.i9?.last_name || '' }}
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Other Last Names Used</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.i9?.other_last_names || 'None' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Date of Birth</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.i9?.date_of_birth || 'N/A' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">U.S. Social Security Number</span>
                <p class="font-bold text-gray-900 dark:text-white font-mono mt-0.5">
                  {{ isSsnRevealed ? (formatFullSsn(taxDocs?.i9?.social_security_number || applicantSsn) || 'N/A') : maskSsn(taxDocs?.i9?.social_security_number || applicantSsn) }}
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 sm:col-span-2">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Address</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ taxDocs?.i9?.address || 'N/A' }} {{ taxDocs?.i9?.apt_number ? 'Apt/Ste ' + taxDocs?.i9?.apt_number : '' }}, {{ taxDocs?.i9?.city || '' }} {{ taxDocs?.i9?.state || 'MD' }} {{ taxDocs?.i9?.zip_code || '' }}
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Email Address</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5 truncate">{{ taxDocs?.i9?.email_address || 'N/A' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Telephone Number</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.i9?.telephone_number || 'N/A' }}</p>
              </div>
            </div>
          </div>

          <!-- I-9 Attestation Details -->
          <div class="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <h4 class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Citizenship & Employment Eligibility Attestation</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Attested Citizenship Status</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5 capitalize">
                  <span v-if="taxDocs?.i9?.citizenship_status === 'citizen'">1. U.S. Citizen</span>
                  <span v-else-if="taxDocs?.i9?.citizenship_status === 'noncitizen_national'">2. U.S. Noncitizen National</span>
                  <span v-else-if="taxDocs?.i9?.citizenship_status === 'lawful_permanent_resident'">3. Lawful Permanent Resident</span>
                  <span v-else-if="taxDocs?.i9?.citizenship_status === 'alien_authorized'">4. Authorized Alien</span>
                  <span v-else>{{ taxDocs?.i9?.citizenship_status || 'Citizen' }}</span>
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Alien / USCIS / Admission #</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.i9?.alien_registration_number || taxDocs?.i9?.work_auth_doc_number || 'N/A' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Work Authorization Expiration</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.i9?.alien_authorized_expiration_date || 'No Expiration (Citizen / Permanent)' }}</p>
              </div>
              <div v-if="taxDocs?.i9?.work_auth_doc_type" class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 sm:col-span-2">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Document Type & Issuing Country</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ taxDocs?.i9?.work_auth_doc_type }} {{ taxDocs?.i9?.foreign_passport_country ? '(' + taxDocs?.i9?.foreign_passport_country + ')' : '' }}
                </p>
              </div>
            </div>
          </div>

          <!-- I-9 Signature -->
          <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
            <div>
              <span class="text-gray-500 dark:text-gray-400">I-9 Employee Attestation Signature:</span>
              <strong class="text-gray-900 dark:text-white font-mono ml-1.5 text-sm">{{ taxDocs?.i9?.employee_signature || application.full_name }}</strong>
            </div>
            <div class="text-gray-500 dark:text-gray-400">
              Date Signed: <strong class="text-gray-800 dark:text-gray-200">{{ taxDocs?.i9?.signature_date || 'N/A' }}</strong>
            </div>
          </div>
        </div>

        <!-- Form W-4 -->
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4 shadow-sm">
          <div class="flex items-center justify-between border-b border-gray-100 dark:border-gray-700 pb-3">
            <div>
              <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
                <Receipt class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
                <span>3. IRS Form W-4 (2022)</span>
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">Employee's Withholding Certificate — Federal Income Tax Withholding</p>
            </div>
            <span class="px-2.5 py-1 rounded-full text-xs font-bold bg-secondary/10 text-secondary dark:text-secondary-dark-text">
              Federal Tax
            </span>
          </div>

          <!-- W-4 Step 1 & 2 -->
          <div class="space-y-2">
            <h4 class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Step 1 & 2: Personal Information & Multiple Jobs</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 sm:col-span-2">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Name on W-4</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ taxDocs?.w4?.first_name_middle_initial || '' }} {{ taxDocs?.w4?.last_name || application.full_name }}
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Social Security Number</span>
                <p class="font-bold text-gray-900 dark:text-white font-mono mt-0.5">
                  {{ isSsnRevealed ? (formatFullSsn(taxDocs?.w4?.social_security_number || applicantSsn) || 'N/A') : maskSsn(taxDocs?.w4?.social_security_number || applicantSsn) }}
                </p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Federal Filing Status</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.w4?.filing_status || 'Single or Married filing separately' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 sm:col-span-2">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Address on W-4</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">{{ taxDocs?.w4?.address || 'N/A' }}, {{ taxDocs?.w4?.city_state_zip || '' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800 sm:col-span-2">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Step 2: Multiple Jobs or Spouse Works</span>
                <p class="font-bold mt-0.5" :class="taxDocs?.w4?.step2_multiple_jobs_or_spouse_works ? 'text-amber-600 dark:text-amber-400' : 'text-gray-700 dark:text-gray-300'">
                  {{ taxDocs?.w4?.step2_multiple_jobs_or_spouse_works ? '✓ Yes (Two Jobs / Spouse Works checked)' : 'No (Standard withholding)' }}
                </p>
              </div>
            </div>
          </div>

          <!-- W-4 Step 3 & 4 Adjustments -->
          <div class="space-y-2 pt-2 border-t border-gray-100 dark:border-gray-700">
            <h4 class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Step 3 & 4: Claim Dependents & Other Adjustments</h4>
            <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Step 3: Qualifying Children under 17</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">${{ taxDocs?.w4?.step3_qualifying_children_amount || '0.00' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Step 3: Other Dependents</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">${{ taxDocs?.w4?.step3_other_dependents_amount || '0.00' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Step 3: Total Dependent Amount</span>
                <p class="font-bold text-emerald-600 dark:text-emerald-400 mt-0.5 text-sm">${{ taxDocs?.w4?.step3_total_dependent_amount || '0.00' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Step 4(a): Other Income (not from jobs)</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">${{ taxDocs?.w4?.step4a_other_income_amount || '0.00' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Step 4(b): Deductions</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">${{ taxDocs?.w4?.step4b_deductions_amount || '0.00' }}</p>
              </div>
              <div class="p-3 rounded-xl bg-gray-50 dark:bg-gray-900/40 border border-gray-100 dark:border-gray-800">
                <span class="text-gray-500 dark:text-gray-400 block font-medium">Step 4(c): Extra Withholding per pay period</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">${{ taxDocs?.w4?.step4c_extra_withholding_amount || '0.00' }}</p>
              </div>
            </div>
          </div>

          <!-- W-4 Signature -->
          <div class="p-3.5 rounded-xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-xs">
            <div>
              <span class="text-gray-500 dark:text-gray-400">W-4 Employee Digital Signature:</span>
              <strong class="text-gray-900 dark:text-white font-mono ml-1.5 text-sm">{{ taxDocs?.w4?.employee_signature || application.full_name }}</strong>
            </div>
            <div class="text-gray-500 dark:text-gray-400">
              Date Signed: <strong class="text-gray-800 dark:text-gray-200">{{ taxDocs?.w4?.signature_date || 'N/A' }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 4: ATTACHED DOCUMENTS -->
      <div v-show="activeTab === 'documents'" class="space-y-4">
        <div class="p-5 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-4">
          <div class="flex items-center justify-between flex-wrap gap-2">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
              <Paperclip class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>Attached Application Documents ({{ localDocuments.length }})</span>
            </h3>

            <BaseButton
              variant="outline"
              size="sm"
              text="Upload Document"
              :icon="UploadCloud"
              icon-position="left"
              @click="isUploadModalOpen = true"
            />
          </div>

          <div v-if="!localDocuments.length" class="p-8 text-center text-sm text-gray-400">
            No document attachments were uploaded for this application.
          </div>

          <div v-else class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div
              v-for="doc in localDocuments"
              :key="doc.document_id"
              class="p-4 rounded-2xl bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 flex items-center justify-between space-x-3"
            >
              <div class="flex items-center space-x-3 min-w-0 flex-1">
                <div class="p-2.5 rounded-xl bg-secondary/10 text-secondary dark:text-secondary-dark-text flex-shrink-0">
                  <FileText class="w-6 h-6" />
                </div>
                <div class="min-w-0 flex-1">
                  <p class="font-bold text-xs sm:text-sm text-gray-900 dark:text-white truncate">
                    {{ doc.file_name }}
                  </p>
                  <div class="flex items-center space-x-2 text-[11px] text-gray-500 mt-0.5 flex-wrap gap-y-1">
                    <span class="capitalize px-1.5 py-0.2 rounded bg-gray-200 dark:bg-gray-700 font-semibold text-[10px]">
                      {{ doc.document_category || 'general' }}
                    </span>
                    <span>{{ formatFileSize(doc.file_size) }}</span>
                    <span
                      v-if="doc.expiry_date"
                      class="px-1.5 py-0.2 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 border border-amber-200/80 dark:border-amber-800/50"
                    >
                      Expires: {{ doc.expiry_date }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Action Buttons (Preview + Download) -->
              <div class="flex items-center space-x-1.5 flex-shrink-0">
                <button
                  type="button"
                  class="p-2 rounded-xl text-secondary dark:text-secondary-dark-text hover:bg-secondary/10 transition"
                  title="Preview Document"
                  @click="handlePreview(doc)"
                >
                  <Eye class="w-4 h-4" />
                </button>
                <button
                  type="button"
                  class="p-2 rounded-xl text-secondary dark:text-secondary-dark-text hover:bg-secondary/10 transition"
                  title="Download File"
                  @click="handleDownload(doc)"
                >
                  <Download class="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 5: REVIEW NOTES & HISTORY -->
      <div v-show="activeTab === 'review_history'" class="space-y-5">
        <div class="p-6 rounded-2xl bg-white dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 space-y-6">
          <div class="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/80 pb-4">
            <h3 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider flex items-center space-x-2">
              <History class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>Admin Review & Decision Status</span>
            </h3>
            <BaseButton
              variant="outline"
              size="sm"
              text="Update Status / Remarks"
              :icon="ClipboardCheck"
              icon-position="left"
              @click="$emit('review', application)"
            />
          </div>

          <!-- Status & Reviewer Summary Grid -->
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <!-- Current Status Pill Card -->
            <div class="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/60 flex flex-col justify-between space-y-2">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">Current Status</span>
              <div>
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold"
                  :class="getStatusBadgeClass(application.application_status)"
                >
                  {{ formatStatus(application.application_status) }}
                </span>
              </div>
            </div>

            <!-- Reviewed By Card -->
            <div class="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/60 flex flex-col justify-between space-y-1">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">Reviewed By</span>
              <p class="font-bold text-sm text-gray-900 dark:text-white truncate">
                {{ application.reviewer?.email || (application.fk_reviewed_by ? `Admin #${application.fk_reviewed_by}` : 'Pending Assignment') }}
              </p>
            </div>

            <!-- Reviewed Date Card -->
            <div class="p-4 rounded-2xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/60 flex flex-col justify-between space-y-1">
              <span class="text-xs font-semibold text-gray-500 dark:text-gray-400">Decision Date</span>
              <p class="font-bold text-sm text-gray-900 dark:text-white">
                {{ application.reviewed_at ? formatDate(application.reviewed_at) : 'Not reviewed yet' }}
              </p>
            </div>
          </div>

          <!-- Admin Remarks Section -->
          <div class="p-5 rounded-2xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/60 space-y-2">
            <div class="flex items-center space-x-2 text-xs font-bold text-gray-700 dark:text-gray-300">
              <FileText class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
              <span>Admin Remarks & Interview Notes</span>
            </div>
            <div
              v-if="application.admin_remarks"
              class="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200/80 dark:border-gray-700 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap leading-relaxed shadow-sm"
            >
              {{ application.admin_remarks }}
            </div>
            <div
              v-else
              class="p-6 rounded-xl bg-white/60 dark:bg-gray-800/40 border border-dashed border-gray-200 dark:border-gray-700 text-center text-xs text-gray-400"
            >
              No remarks or notes have been logged for this application yet.
            </div>
          </div>

          <!-- Staff Account Provisioning Status Card (for Approved Applications or Paper Records) -->
          <div
            v-if="application.application_status === 'approved' || application.record_type === 'paper' || application.fk_created_user_id || application.fk_linked_user_id"
            class="p-5 rounded-2xl border space-y-3"
            :class="[
              (application.fk_created_user_id || application.fk_linked_user_id)
                ? 'bg-emerald-50/70 dark:bg-emerald-950/30 border-emerald-200/80 dark:border-emerald-800/50'
                : 'bg-amber-50/70 dark:bg-amber-950/30 border-amber-200/80 dark:border-amber-800/50'
            ]"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center space-x-2">
                <UserPlus class="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                <span class="text-xs font-bold uppercase tracking-wider text-gray-800 dark:text-gray-200">
                  Staff Account Provisioning
                </span>
              </div>
              <span
                class="px-2.5 py-0.5 rounded-full text-xs font-bold capitalize"
                :class="(application.fk_created_user_id || application.fk_linked_user_id) ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/60 dark:text-emerald-300' : 'bg-amber-100 text-amber-800 dark:bg-amber-900/60 dark:text-amber-300'"
              >
                {{ (application.fk_created_user_id || application.fk_linked_user_id) ? 'Account Active' : 'Account Pending' }}
              </span>
            </div>

            <div v-if="application.fk_created_user_id || application.fk_linked_user_id" class="text-xs text-emerald-900 dark:text-emerald-200 space-y-1">
              <p>
                A staff portal account has been provisioned and linked to this application.
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1 font-mono text-[11px]">
                <div><span class="text-gray-500">Staff User ID:</span> #{{ application.fk_created_user_id || application.fk_linked_user_id }}</div>
                <div><span class="text-gray-500">Account Email:</span> {{ application.created_user?.email || application.email }}</div>
              </div>
            </div>

            <div v-else class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-amber-900 dark:text-amber-200">
              <p>
                {{ application.record_type === 'paper' ? 'This paper record is not linked to any staff member. You can provision their staff portal account and email login credentials now.' : 'This candidate is approved. You can provision their staff portal account and email login credentials now.' }}
              </p>
              <BaseButton
                variant="primary"
                size="sm"
                text="Create Staff Account Now"
                :icon="UserPlus"
                icon-position="left"
                class="flex-shrink-0"
                @click="$emit('create-account', application)"
              />
            </div>
          </div>

          <!-- Applicant Edit History & Secure Access Section (Online Applications Only) -->
          <div
            v-if="application.record_type !== 'paper'"
            class="p-5 rounded-2xl bg-gray-50/80 dark:bg-gray-900/50 border border-gray-200/70 dark:border-gray-700/60 space-y-4"
          >
            <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200/70 dark:border-gray-700/60 pb-3">
              <div class="flex items-center space-x-2 text-xs font-bold text-gray-800 dark:text-gray-200 uppercase tracking-wider">
                <History class="w-4 h-4 text-secondary dark:text-secondary-dark-text" />
                <span>Applicant Secure Edit Access & Audit Trail</span>
              </div>
              <div class="flex items-center space-x-2">
                <BaseButton
                  v-if="application.is_editable_by_applicant"
                  variant="outline"
                  size="sm"
                  text="Resend Edit Link"
                  :icon="Send"
                  icon-position="left"
                  :loading="isResendingLink"
                  @click="handleResendAccessLink"
                />
                <BaseButton
                  v-if="application.has_active_access_link"
                  variant="outline"
                  size="sm"
                  text="Revoke Access"
                  :icon="KeyRound"
                  icon-position="left"
                  :loading="isRevokingAccess"
                  class="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50 dark:hover:bg-red-950/40"
                  @click="handleRevokeAccess"
                />
              </div>
            </div>

            <!-- Access Link Status Indicators -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div class="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700 shadow-sm">
                <span class="text-[10px] text-gray-400 uppercase font-semibold">Edit Access Status</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ application.is_editable_by_applicant ? 'Active / Allowed' : 'Locked (Read-Only / Closed)' }}
                </p>
              </div>
              <div class="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700 shadow-sm">
                <span class="text-[10px] text-gray-400 uppercase font-semibold">Total Edits Saved</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ application.applicant_edit_count || 0 }} update session(s)
                </p>
              </div>
              <div class="p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200/80 dark:border-gray-700 shadow-sm">
                <span class="text-[10px] text-gray-400 uppercase font-semibold">Last Applicant Edit</span>
                <p class="font-bold text-gray-900 dark:text-white mt-0.5">
                  {{ application.last_edited_by_applicant_at ? formatDate(application.last_edited_by_applicant_at) : 'No updates logged' }}
                </p>
              </div>
            </div>

            <!-- Audit Log Table -->
            <div class="space-y-2">
              <h4 class="text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                Modification History Log
              </h4>
              <div
                v-if="!application.edit_logs || application.edit_logs.length === 0"
                class="p-5 rounded-xl bg-white/70 dark:bg-gray-800/50 border border-dashed border-gray-200 dark:border-gray-700 text-center text-xs text-gray-400"
              >
                No applicant modification records logged yet.
              </div>
              <div
                v-else
                class="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm"
              >
                <table class="w-full text-xs text-left">
                  <thead class="bg-gray-50 dark:bg-gray-900/60 text-[10px] uppercase font-bold text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <tr>
                      <th class="p-3">Date & Time</th>
                      <th class="p-3">Modified Sections</th>
                      <th class="p-3">IP Address</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-100 dark:divide-gray-700/60 text-gray-700 dark:text-gray-300">
                    <tr
                      v-for="log in application.edit_logs"
                      :key="log.edit_log_id"
                      class="hover:bg-gray-50/50 dark:hover:bg-gray-700/30"
                    >
                      <td class="p-3 font-medium whitespace-nowrap">{{ formatDate(log.created_at) }}</td>
                      <td class="p-3">
                        <div class="flex flex-wrap gap-1">
                          <span
                            v-for="section in log.changed_sections"
                            :key="section"
                            class="px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 uppercase"
                          >
                            {{ section.replace(/_/g, ' ') }}
                          </span>
                        </div>
                      </td>
                      <td class="p-3 font-mono text-[11px] text-gray-500">{{ log.ip_address || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- TAB 6: PRINT & DOWNLOADABLE PDF PREVIEW -->
      <div v-show="activeTab === 'print_pdf'" class="space-y-4">
        <!-- PDF Action Toolbar -->
        <div class="p-4 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div class="flex items-center space-x-2 text-sm text-gray-700 dark:text-gray-300">
            <Printer class="w-5 h-5 text-secondary dark:text-secondary-dark-text" />
            <span class="font-bold">Official Application PDF & Print Document</span>
          </div>

          <div class="flex items-center space-x-2 w-full sm:w-auto justify-end flex-wrap gap-2">
            <!-- SSN Mask / Reveal Toggle Button for Print View (Online applications only) -->
            <button
              v-if="!isPaperRecord"
              type="button"
              @click="isPrintSsnMasked = !isPrintSsnMasked"
              class="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-xl border transition-colors cursor-pointer"
              :class="isPrintSsnMasked 
                ? 'bg-amber-50 text-amber-800 border-amber-300 hover:bg-amber-100 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800' 
                : 'bg-emerald-50 text-emerald-800 border-emerald-300 hover:bg-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800'"
              :title="isPrintSsnMasked ? 'SSN is masked with asterisks. Click to reveal full SSN.' : 'SSN is revealed. Click to mask SSN.'"
            >
              <EyeOff v-if="isPrintSsnMasked" class="w-3.5 h-3.5 mr-1.5 text-amber-600 dark:text-amber-400" />
              <Eye v-else class="w-3.5 h-3.5 mr-1.5 text-emerald-600 dark:text-emerald-400" />
              {{ isPrintSsnMasked ? 'Print SSN: Masked' : 'Print SSN: Revealed' }}
            </button>

            <BaseButton
              variant="outline"
              size="sm"
              text="Print Document"
              :icon="Printer"
              icon-position="left"
              @click="handleRequestPrint"
            />
            <BaseButton
              variant="outline"
              size="sm"
              text="Open PDF Tab"
              :icon="ExternalLink"
              icon-position="left"
              @click="handleOpenPdfNewTab"
            />
          </div>
        </div>

        <!-- Printable Document Canvas -->
        <div
          id="printable-application"
          class="bg-white text-gray-900 border border-gray-300 rounded-2xl p-6 sm:p-10 shadow-sm space-y-6 max-w-4xl mx-auto printable-area"
        >
          <!-- Document Header -->
          <div class="border-b-2 border-secondary pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 class="text-2xl font-black text-secondary uppercase tracking-tight">
                Jeducare Services
              </h1>
              <p class="text-xs text-gray-500 font-semibold tracking-wide uppercase mt-0.5">
                {{ isPaperRecord ? 'Official Digitized Employment Record' : 'Official Employment Application Record' }}
              </p>
            </div>
            <div class="text-right sm:text-right text-xs text-gray-600">
              <p class="font-mono font-bold text-sm text-gray-900">APPLICATION #{{ application.application_id }}</p>
              <p class="text-[11px] text-gray-500 mt-0.5">
                {{ isPaperRecord ? 'Digitized: ' : 'Submitted: ' }}{{ formatDate(application.submitted_at || application.created_at) }}
              </p>
              <div class="mt-1.5 flex items-center justify-end space-x-1.5">
                <span class="text-[11px] text-gray-500 font-medium leading-none">Status:</span>
                <span
                  class="px-2.5 py-0.5 rounded text-[10px] font-bold uppercase inline-flex items-center leading-normal"
                  :class="getStatusBadgeClass(application.application_status)"
                >
                  {{ formatStatus(application.application_status) }}
                </span>
                <span
                  v-if="isPaperRecord"
                  class="px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-purple-100 text-purple-800"
                >
                  Paper Record
                </span>
              </div>
            </div>
          </div>

          <!-- ============================================== -->
          <!-- PAPER PRINTABLE LAYOUT (record_type === 'paper') -->
          <!-- ============================================== -->
          <template v-if="isPaperRecord">
            <!-- Section 1: Applicant Profile & Contact -->
            <div class="space-y-3">
              <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1 flex items-center space-x-1.5">
                <span>1. Applicant Profile & Contact</span>
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Full Legal Name</span>
                  <p class="font-bold text-sm text-gray-900">{{ application.full_name }}</p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Email Address</span>
                  <p class="font-bold text-gray-900 break-all">
                    {{ isPlaceholderEmail(application.email) ? 'Not provided (Digitized Paper Record)' : (application.email || 'Not provided') }}
                  </p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Phone Number</span>
                  <p class="font-bold text-gray-900">{{ application.phone_number || 'Not provided' }}</p>
                </div>
                <div class="sm:col-span-2">
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Home / Street Address</span>
                  <p class="font-bold text-gray-900">
                    {{ formatAddress(application) || 'Not provided' }}
                  </p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Application Source</span>
                  <p class="font-bold text-purple-700">Offline / Paper Record (Digitized)</p>
                </div>
              </div>
            </div>

            <!-- Section 2: Position & Target Availability -->
            <div class="space-y-3">
              <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1 flex items-center space-x-1.5">
                <span>2. Position & Target Availability</span>
              </h3>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Position Applied / Hired For</span>
                  <p class="font-bold text-gray-900">{{ application.position_applied_for || 'Not specified' }}</p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Availability / Start Date</span>
                  <p class="font-bold text-gray-900">{{ application.availability_date ? formatDate(application.availability_date) : 'Not specified' }}</p>
                </div>
              </div>
            </div>

            <!-- Section 3: Previous Experience Summary -->
            <div class="space-y-2">
              <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
                3. Previous Experience Summary
              </h3>
              <div class="p-3.5 rounded bg-gray-50 border border-gray-200 text-xs">
                <p class="text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {{ application.previous_experience || 'No previous experience summary was entered for this paper record.' }}
                </p>
              </div>
            </div>

            <!-- Section 4: Attached Scanned Paperwork Manifest -->
            <div class="space-y-2">
              <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
                4. Attached Scanned Documents & Paperwork ({{ localDocuments.length }})
              </h3>
              <div v-if="!localDocuments.length" class="text-xs text-gray-500 italic p-2 bg-gray-50 rounded border border-gray-200">
                No document attachments were uploaded.
              </div>
              <div v-else class="border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-200 text-xs">
                <div
                  v-for="doc in localDocuments"
                  :key="doc.document_id"
                  class="p-2.5 flex items-center justify-between bg-white hover:bg-gray-50"
                >
                  <div class="flex items-center space-x-2 min-w-0">
                    <span class="font-bold text-gray-900 truncate">{{ doc.file_name }}</span>
                    <span class="text-[10px] uppercase font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                      {{ doc.document_category?.replace(/_/g, ' ') || 'paper record' }}
                    </span>
                    <span class="text-gray-400 text-[10px]">{{ formatFileSize(doc.file_size) }}</span>
                  </div>
                  <div v-if="doc.expiry_date" class="text-right flex-shrink-0">
                    <span class="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                      Exp: {{ doc.expiry_date }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section 5: Administrative Record & Remarks -->
            <div class="space-y-2">
              <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
                5. Administrative Record & Remarks
              </h3>
              <div class="p-3 rounded bg-gray-50 border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Record Status</span>
                  <p class="font-bold mt-0.5">{{ formatStatus(application.application_status) }}</p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Digitized / Reviewed By</span>
                  <p class="font-bold mt-0.5 truncate">{{ application.reviewer?.email || (application.fk_reviewed_by ? `Admin #${application.fk_reviewed_by}` : 'Admin') }}</p>
                </div>
                <div>
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Record Date</span>
                  <p class="font-bold mt-0.5">{{ formatDate(application.reviewed_at || application.submitted_at || application.created_at) }}</p>
                </div>
                <div v-if="application.fk_linked_user_id || application.fk_created_user_id" class="sm:col-span-3 pt-2 border-t border-gray-200">
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Linked Staff Account</span>
                  <p class="font-bold text-gray-900 mt-0.5">Staff User #{{ application.fk_linked_user_id || application.fk_created_user_id }}</p>
                </div>
                <div v-if="application.admin_remarks" class="sm:col-span-3 pt-2 border-t border-gray-200">
                  <span class="text-[10px] text-gray-500 uppercase font-semibold">Admin Remarks / Notes</span>
                  <p class="text-gray-800 text-xs mt-0.5 whitespace-pre-wrap">{{ application.admin_remarks }}</p>
                </div>
              </div>
            </div>
          </template>

          <!-- ============================================== -->
          <!-- ONLINE APPLICATION PRINTABLE (record_type !== 'paper') -->
          <!-- ============================================== -->
          <template v-else>

          <!-- Section 1: Applicant Profile & Contact -->
          <div class="space-y-3">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1 flex items-center space-x-1.5">
              <span>1. Applicant Profile & Contact</span>
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Full Legal Name</span>
                <p class="font-bold text-sm text-gray-900">{{ application.full_name }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Email Address</span>
                <p class="font-bold text-gray-900 break-all">{{ application.email }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Phone Number</span>
                <p class="font-bold text-gray-900">{{ application.phone_number || 'N/A' }}</p>
              </div>
              <div class="sm:col-span-2">
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Street Address</span>
                <p class="font-bold text-gray-900">
                  {{ application.street_address }}, {{ application.city }}, {{ application.state }} {{ application.zip_code }}
                </p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Date of Birth</span>
                <p class="font-bold text-gray-900">{{ jobApp?.date_of_birth || 'N/A' }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Social Security Number</span>
                <p class="font-bold text-gray-900 font-mono">
                  {{ isPrintSsnMasked ? maskSsn(applicantSsn) : (formatFullSsn(applicantSsn) || 'N/A') }}
                </p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">NPI Number</span>
                <p class="font-bold text-gray-900">{{ jobApp?.npi_number || 'None' }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">US Work Authorization</span>
                <p class="font-bold text-gray-900">{{ jobApp?.is_legally_eligible_to_work ? 'Yes (Authorized to work in US)' : 'No' }}</p>
              </div>
            </div>
          </div>

          <!-- Section 2: Position & Availability -->
          <div class="space-y-2">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              2. Position Applied & Availability
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Position Applied</span>
                <p class="font-bold text-gray-900">{{ application.position_applied_for || 'General Application' }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Employment Desired</span>
                <p class="font-bold text-gray-900">{{ jobApp?.employment_desired || 'N/A' }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Weekly Hours Available</span>
                <p class="font-bold text-gray-900">{{ jobApp?.weekly_hours_available || 40 }} hrs/wk</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Start Date</span>
                <p class="font-bold text-gray-900">{{ application.availability_date || jobApp?.date_available_for_work || 'Immediately' }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Driver's License</span>
                <p class="font-bold text-gray-900">
                  {{ jobApp?.has_driver_license ? `Yes (${jobApp?.driver_license_state} #${jobApp?.driver_license_number || 'N/A'})` : 'No' }}
                </p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Transportation</span>
                <p class="font-bold text-gray-900">{{ jobApp?.transportation_means || 'Personal Vehicle' }}</p>
              </div>
            </div>

            <!-- Days & Hours Schedule -->
            <div class="p-2 rounded bg-gray-50 border border-gray-200 text-xs">
              <span class="text-[10px] text-gray-500 uppercase font-semibold block mb-1">
                Days & Hours Schedule {{ jobApp?.availability_no_pref ? '(Open Availability)' : '' }}
              </span>
              <div class="grid grid-cols-7 gap-1 text-[11px] text-center">
                <div class="p-1 bg-white border border-gray-200 rounded"><span class="block text-[9px] text-gray-400 font-bold">MON</span>{{ jobApp?.available_mon || '—' }}</div>
                <div class="p-1 bg-white border border-gray-200 rounded"><span class="block text-[9px] text-gray-400 font-bold">TUE</span>{{ jobApp?.available_tue || '—' }}</div>
                <div class="p-1 bg-white border border-gray-200 rounded"><span class="block text-[9px] text-gray-400 font-bold">WED</span>{{ jobApp?.available_wed || '—' }}</div>
                <div class="p-1 bg-white border border-gray-200 rounded"><span class="block text-[9px] text-gray-400 font-bold">THU</span>{{ jobApp?.available_thur || '—' }}</div>
                <div class="p-1 bg-white border border-gray-200 rounded"><span class="block text-[9px] text-gray-400 font-bold">FRI</span>{{ jobApp?.available_fri || '—' }}</div>
                <div class="p-1 bg-white border border-gray-200 rounded"><span class="block text-[9px] text-gray-400 font-bold">SAT</span>{{ jobApp?.available_sat || '—' }}</div>
                <div class="p-1 bg-white border border-gray-200 rounded"><span class="block text-[9px] text-gray-400 font-bold">SUN</span>{{ jobApp?.available_sun || '—' }}</div>
              </div>
            </div>
          </div>

          <!-- Section 3: Education History -->
          <div class="space-y-2">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              3. Education History
            </h3>
            <table class="w-full text-xs text-left border border-gray-200">
              <thead class="bg-gray-100 uppercase text-[10px] font-bold text-gray-600">
                <tr>
                  <th class="p-2 border-r border-gray-200">Level</th>
                  <th class="p-2 border-r border-gray-200">School Name</th>
                  <th class="p-2 border-r border-gray-200">Location</th>
                  <th class="p-2 border-r border-gray-200">Years</th>
                  <th class="p-2">Major / Degree</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                <tr v-for="(edu, idx) in jobApp?.education_history || []" :key="idx">
                  <td class="p-2 font-bold border-r border-gray-200">{{ edu.type }}</td>
                  <td class="p-2 border-r border-gray-200">{{ edu.name_of_school || '—' }}</td>
                  <td class="p-2 border-r border-gray-200">{{ edu.location || '—' }}</td>
                  <td class="p-2 border-r border-gray-200">{{ edu.years_completed || '—' }}</td>
                  <td class="p-2">{{ edu.major_degree || '—' }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Section 4: Computer & Specialized Skills -->
          <div class="space-y-2">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              4. Computer & Administrative Skills
            </h3>
            <div class="p-2.5 rounded bg-gray-50 border border-gray-200 space-y-1.5 text-xs">
              <div class="flex flex-wrap gap-1.5 text-[11px]">
                <span v-if="jobApp?.skill_windows" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ Windows OS</span>
                <span v-if="jobApp?.skill_word" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ MS Word</span>
                <span v-if="jobApp?.skill_excel" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ MS Excel</span>
                <span v-if="jobApp?.skill_powerpoint" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ PowerPoint</span>
                <span v-if="jobApp?.skill_access" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ MS Access</span>
                <span v-if="jobApp?.skill_publisher" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ MS Publisher</span>
                <span v-if="jobApp?.skill_typing" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ Fast Typing</span>
                <span v-if="jobApp?.skill_wordperfect" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ WordPerfect</span>
                <span v-if="jobApp?.skill_pc" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ PC</span>
                <span v-if="jobApp?.skill_mac" class="px-2 py-0.5 rounded bg-white border border-gray-300 font-semibold">✓ Mac</span>
              </div>
              <p v-if="jobApp?.other_skills" class="text-[11px] text-gray-700 pt-0.5">
                <strong>Other Skills / Certs:</strong> {{ jobApp.other_skills }}
              </p>
            </div>
          </div>

          <!-- Section 5: Work Experience History -->
          <div class="space-y-3">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              5. Work Experience History
            </h3>
            <div v-if="!jobApp?.work_experiences?.length" class="text-xs text-gray-400 italic">
              {{ application.previous_experience || 'No previous work experiences recorded.' }}
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="(work, idx) in jobApp.work_experiences"
                :key="idx"
                class="p-3 rounded border border-gray-200 text-xs bg-gray-50/50 space-y-1"
              >
                <div class="flex justify-between items-center font-bold text-gray-900">
                  <span>{{ work.name_of_employer || `Employer #${idx + 1}` }}</span>
                  <span class="text-gray-500 font-normal">{{ work.from_date }} — {{ work.to_date || 'Present' }}</span>
                </div>
                <div class="text-[11px] text-gray-600">
                  <span>Title: <strong>{{ work.last_job_title || 'N/A' }}</strong></span>
                  <span class="mx-2">•</span>
                  <span>Supervisor: <strong>{{ work.last_supervisor || 'N/A' }}</strong></span>
                  <span class="mx-2">•</span>
                  <span>Phone: <strong>{{ work.phone_number || 'N/A' }}</strong></span>
                </div>
                <p v-if="work.duties_skills_promotions" class="text-[11px] text-gray-600 pt-0.5">
                  Duties / Skills: {{ work.duties_skills_promotions }}
                </p>
              </div>
            </div>
          </div>

          <!-- Section 6: Employment References -->
          <div class="space-y-2">
            <div class="flex items-center justify-between border-b border-gray-200 pb-1">
              <h3 class="text-xs font-bold uppercase tracking-wider text-secondary">
                6. Employment References (3 References)
              </h3>
              <span
                v-if="displayReferences?.length"
                class="text-[10px] font-bold"
                :class="verifiedReferencesCount === displayReferences.length ? 'text-emerald-700' : 'text-amber-700'"
              >
                {{ verifiedReferencesCount }} of {{ displayReferences.length }} Verified
              </span>
            </div>
            <div v-if="!displayReferences?.length" class="text-xs text-gray-400 italic">
              No references recorded.
            </div>
            <div v-else class="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div
                v-for="(refItem, idx) in displayReferences"
                :key="idx"
                class="p-2.5 rounded border text-xs space-y-1"
                :class="refItem.is_verified ? 'border-emerald-300 bg-emerald-50/40' : 'border-gray-200 bg-gray-50/50'"
              >
                <div class="flex items-center justify-between">
                  <span class="font-bold text-gray-900 block truncate">{{ refItem.name || `Reference #${idx + 1}` }}</span>
                  <span
                    class="px-1.5 py-0.2 rounded text-[9px] font-bold"
                    :class="refItem.is_verified ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'"
                  >
                    {{ refItem.is_verified ? '✓ Verified' : 'Pending' }}
                  </span>
                </div>
                <p class="text-[11px] text-gray-600">{{ refItem.position_title }} • {{ refItem.organization }}</p>
                <p class="text-[10px] text-gray-500">Phone: {{ refItem.phone_number || 'N/A' }} | Email: {{ refItem.email || 'N/A' }}</p>
                <p v-if="refItem.is_verified && refItem.verified_by" class="text-[9.5px] text-emerald-800 font-medium">
                  Verified by {{ refItem.verified_by }}
                </p>
                <p v-if="refItem.verification_notes" class="text-[9.5px] text-gray-600 italic">
                  Note: {{ refItem.verification_notes }}
                </p>
              </div>
            </div>
          </div>

          <!-- Section 7: Background Check & Digital Waivers -->
          <div class="space-y-2">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              7. Background Check & Digital Waivers
            </h3>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div class="p-2.5 rounded bg-gray-50 border border-gray-200">
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Background Check Acknowledged</span>
                <p class="font-bold text-gray-900 mt-0.5">
                  {{ application.background_check_data?.acknowledges_requirements ? '✓ Confirmed & Accepted Requirements' : '✗ Not Acknowledged' }}
                </p>
                <p class="text-[10px] text-gray-500 mt-0.5">Signed: {{ application.background_check_data?.applicant_name || application.full_name }}</p>
              </div>
              <div class="p-2.5 rounded bg-gray-50 border border-gray-200">
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Criminal History Disclosure</span>
                <p class="font-bold mt-0.5" :class="jobApp?.has_been_convicted_of_crime ? 'text-red-600' : 'text-gray-900'">
                  {{ jobApp?.has_been_convicted_of_crime ? 'Yes - Conviction Disclosed' : 'No criminal convictions disclosed' }}
                </p>
                <p v-if="jobApp?.crime_conviction_explanation" class="text-[10px] text-red-500 mt-0.5">
                  {{ jobApp.crime_conviction_explanation }}
                </p>
              </div>
              <div class="p-2.5 rounded bg-gray-50 border border-gray-200">
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Application Waiver Signature</span>
                <p class="font-bold text-gray-900 font-mono mt-0.5">{{ jobApp?.waiver_signature || 'N/A' }}</p>
                <p class="text-[10px] text-gray-500">Date: {{ jobApp?.waiver_date }}</p>
              </div>
              <div class="p-2.5 rounded bg-gray-50 border border-gray-200">
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Job Description Signature</span>
                <p class="font-bold text-gray-900 font-mono mt-0.5">{{ jobApp?.job_description_signature || 'N/A' }}</p>
                <p class="text-[10px] text-gray-500">Date: {{ jobApp?.job_description_date }}</p>
              </div>
            </div>
          </div>

          <!-- Section 8: Tax Documentation Summary -->
          <div class="space-y-3">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              8. Complete Tax Declarations & Documentation (MW507, I-9, W-4)
            </h3>

            <!-- 8.1 MW507 Details -->
            <div class="p-2.5 rounded border border-gray-200 bg-gray-50/50 space-y-1.5 text-xs">
              <div class="flex justify-between items-center font-bold text-gray-900 border-b border-gray-200/80 pb-1">
                <span>Maryland Form MW507 (State Withholding Exemption)</span>
                <span class="text-[10px] text-gray-500 font-normal">Signed: {{ taxDocs?.mw507?.employee_signature || 'N/A' }} ({{ taxDocs?.mw507?.signature_date || 'N/A' }})</span>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-gray-700">
                <div>
                  <span class="text-gray-400 block text-[10px]">Name / SSN:</span>
                  <strong>{{ taxDocs?.mw507?.full_name || application.full_name }}</strong>
                  <span class="text-[10px] text-gray-500 font-mono block">
                    SSN: {{ isPrintSsnMasked ? maskSsn(taxDocs?.mw507?.social_security_number || applicantSsn) : (formatFullSsn(taxDocs?.mw507?.social_security_number || applicantSsn) || 'N/A') }}
                  </span>
                </div>
                <div><span class="text-gray-400 block text-[10px]">Filing Status:</span><strong>{{ taxDocs?.mw507?.filing_status || 'Single' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">County:</span><strong>{{ taxDocs?.mw507?.county_of_residence || 'N/A' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Line 1 Exemptions:</span><strong>{{ taxDocs?.mw507?.line1_total_exemptions ?? '1' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Line 2 Addl Withholding:</span><strong>${{ taxDocs?.mw507?.line2_additional_withholding || '0' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Line 3 Exemption:</span><strong>{{ (taxDocs?.mw507?.line3_a_last_year_no_tax || taxDocs?.mw507?.line3_b_this_year_no_tax) ? 'Yes (Claimed)' : 'None' }}</strong></div>
                <div class="sm:col-span-2"><span class="text-gray-400 block text-[10px]">Reciprocity / Military Spouse State:</span><strong>{{ taxDocs?.mw507?.line4_domiciled_state || taxDocs?.mw507?.line8_military_spouse_state || 'MD Resident' }}</strong></div>
              </div>
            </div>

            <!-- 8.2 I-9 Details -->
            <div class="p-2.5 rounded border border-gray-200 bg-gray-50/50 space-y-1.5 text-xs">
              <div class="flex justify-between items-center font-bold text-gray-900 border-b border-gray-200/80 pb-1">
                <span>USCIS Form I-9 (Employment Eligibility Verification)</span>
                <span class="text-[10px] text-gray-500 font-normal">Signed: {{ taxDocs?.i9?.employee_signature || 'N/A' }} ({{ taxDocs?.i9?.signature_date || 'N/A' }})</span>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-gray-700">
                <div><span class="text-gray-400 block text-[10px]">Employee Name:</span><strong>{{ taxDocs?.i9?.first_name }} {{ taxDocs?.i9?.middle_initial ? taxDocs?.i9?.middle_initial + '.' : '' }} {{ taxDocs?.i9?.last_name }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Other Names:</span><strong>{{ taxDocs?.i9?.other_last_names || 'None' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Date of Birth:</span><strong>{{ taxDocs?.i9?.date_of_birth || 'N/A' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Telephone:</span><strong>{{ taxDocs?.i9?.telephone_number || 'N/A' }}</strong></div>
                <div class="sm:col-span-2"><span class="text-gray-400 block text-[10px]">Citizenship Status:</span><strong>{{ taxDocs?.i9?.citizenship_status ? taxDocs?.i9?.citizenship_status.replace(/_/g, ' ').toUpperCase() : 'CITIZEN' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Alien / USCIS #:</span><strong>{{ taxDocs?.i9?.alien_registration_number || taxDocs?.i9?.work_auth_doc_number || 'N/A' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Work Auth Expiration:</span><strong>{{ taxDocs?.i9?.alien_authorized_expiration_date || 'N/A (No Expiration)' }}</strong></div>
              </div>
            </div>

            <!-- 8.3 W-4 Details -->
            <div class="p-2.5 rounded border border-gray-200 bg-gray-50/50 space-y-1.5 text-xs">
              <div class="flex justify-between items-center font-bold text-gray-900 border-b border-gray-200/80 pb-1">
                <span>IRS Form W-4 (Federal Income Tax Withholding)</span>
                <span class="text-[10px] text-gray-500 font-normal">Signed: {{ taxDocs?.w4?.employee_signature || 'N/A' }} ({{ taxDocs?.w4?.signature_date || 'N/A' }})</span>
              </div>
              <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] text-gray-700">
                <div><span class="text-gray-400 block text-[10px]">Name on W-4:</span><strong>{{ taxDocs?.w4?.first_name_middle_initial }} {{ taxDocs?.w4?.last_name }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Federal Filing Status:</span><strong>{{ taxDocs?.w4?.filing_status || 'Single' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Step 2 Multiple Jobs:</span><strong>{{ taxDocs?.w4?.step2_multiple_jobs_or_spouse_works ? 'Yes (Checked)' : 'No' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Step 3 Total Dependents:</span><strong>${{ taxDocs?.w4?.step3_total_dependent_amount || '0.00' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Step 3 Qualifying Children:</span><strong>${{ taxDocs?.w4?.step3_qualifying_children_amount || '0.00' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Step 3 Other Dependents:</span><strong>${{ taxDocs?.w4?.step3_other_dependents_amount || '0.00' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Step 4(a) Other Income:</span><strong>${{ taxDocs?.w4?.step4a_other_income_amount || '0.00' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Step 4(b) Deductions:</span><strong>${{ taxDocs?.w4?.step4b_deductions_amount || '0.00' }}</strong></div>
                <div><span class="text-gray-400 block text-[10px]">Step 4(c) Extra Withholding:</span><strong>${{ taxDocs?.w4?.step4c_extra_withholding_amount || '0.00' }}</strong></div>
              </div>
            </div>
          </div>

          <!-- Section 9: Attached Application Documents -->
          <div class="space-y-2">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              9. Attached Documents ({{ localDocuments.length }})
            </h3>
            <div v-if="!localDocuments.length" class="text-xs text-gray-400 italic">
              No attachments uploaded with this application.
            </div>
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
              <div
                v-for="doc in localDocuments"
                :key="doc.document_id"
                class="p-2.5 rounded border border-gray-200 bg-gray-50/50 flex items-center justify-between"
              >
                <div class="truncate mr-2">
                  <span class="font-bold text-gray-900 block truncate">{{ doc.file_name }}</span>
                  <span class="text-[10px] text-gray-500 capitalize">{{ doc.document_category || 'general' }} • {{ formatFileSize(doc.file_size) }}</span>
                </div>
                <div v-if="doc.expiry_date" class="text-right flex-shrink-0">
                  <span class="text-[10px] font-semibold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                    Exp: {{ doc.expiry_date }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Section 10: Administrative Review Decision -->
          <div class="space-y-2">
            <h3 class="text-xs font-bold uppercase tracking-wider text-secondary border-b border-gray-200 pb-1">
              10. Administrative Review & Status
            </h3>
            <div class="p-3 rounded bg-gray-50 border border-gray-200 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Decision Status</span>
                <p class="font-bold mt-0.5">{{ formatStatus(application.application_status) }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Reviewed By</span>
                <p class="font-bold mt-0.5 truncate">{{ application.reviewer?.email || (application.fk_reviewed_by ? `Admin #${application.fk_reviewed_by}` : 'Pending') }}</p>
              </div>
              <div>
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Decision Date</span>
                <p class="font-bold mt-0.5">{{ application.reviewed_at ? formatDate(application.reviewed_at) : 'Not reviewed yet' }}</p>
              </div>
              <div v-if="application.admin_remarks" class="sm:col-span-3 pt-2 border-t border-gray-200">
                <span class="text-[10px] text-gray-500 uppercase font-semibold">Admin Remarks</span>
                <p class="text-gray-800 text-xs mt-0.5 whitespace-pre-wrap">{{ application.admin_remarks }}</p>
              </div>
            </div>
          </div>
          </template>

          <!-- Document Sub-footer -->
          <div class="pt-4 border-t border-gray-200 text-center text-[10px] text-gray-400">
            Jeducare Portal • Application Ref #{{ application.application_id }} • Confidential Document
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Footer -->
    <template #footer>
      <div class="flex items-center justify-between w-full">
        <BaseButton
          variant="outline"
          size="md"
          text="Close"
          @click="closeModal"
        />
        <div class="flex items-center space-x-2.5">
          <!-- Create Staff Account Button in Footer (if Approved or Paper record and no account yet) -->
          <BaseButton
            v-if="canCreateStaffAccount(application)"
            variant="success"
            size="md"
            text="Create Staff"
            :icon="UserPlus"
            icon-position="left"
            @click="$emit('create-account', application)"
          />

          <!-- Single Consolidated Review Button -->
          <BaseButton
            variant="primary"
            size="md"
            text="Review Application"
            :icon="ClipboardCheck"
            icon-position="left"
            @click="$emit('review', application)"
          />
        </div>
      </div>
    </template>

    <!-- Print Confirmation / Permission Modal -->
    <PrintConfirmModal
      :is-visible="isPrintConfirmVisible"
      :application="application"
      :initial-mask-ssn="isPrintSsnMasked"
      @close="isPrintConfirmVisible = false"
      @confirm="handleConfirmPrint"
    />

    <!-- Application Document Inline Preview Modal -->
    <ViewApplicationDocumentModal
      :is-visible="isPreviewModalVisible"
      :document="selectedDocForPreview"
      @close="isPreviewModalVisible = false"
    />

    <!-- Upload Additional Documents Modal -->
    <BaseModal
      :is-visible="isUploadModalOpen"
      title="Upload Additional Documents"
      subtitle="Attach scanned paperwork, certificates, or IDs to this application record."
      :icon="UploadCloud"
      size="lg"
      :show-footer="true"
      @close="closeUploadModal"
    >
      <div class="space-y-4">
        <!-- Dropzone -->
        <div
          class="border-2 border-dashed rounded-2xl p-6 text-center cursor-pointer transition-colors"
          :class="isDragging ? 'border-secondary bg-secondary/5' : 'border-gray-300 dark:border-gray-600 hover:border-secondary/60'"
          @dragover.prevent="isDragging = true"
          @dragleave.prevent="isDragging = false"
          @drop.prevent="handleExtraFileDrop"
          @click="triggerExtraFileInput"
        >
          <input
            ref="extraFileInputRef"
            type="file"
            multiple
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            class="hidden"
            @change="handleExtraFileInputChange"
          />
          <UploadCloud class="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p class="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Click to select files or drag and drop here
          </p>
          <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Supported formats: PDF, DOC, DOCX, JPG, PNG (Max 32MB per file)
          </p>
        </div>

        <!-- Staged files list -->
        <div v-if="newUploadFiles.length > 0" class="space-y-2 max-h-60 overflow-y-auto pr-1">
          <div
            v-for="(item, idx) in newUploadFiles"
            :key="idx"
            class="p-3 rounded-xl bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs"
          >
            <div class="min-w-0 flex-1">
              <p class="font-bold text-gray-900 dark:text-white truncate">
                {{ item.file.name }}
              </p>
              <p class="text-[11px] text-gray-500">
                {{ formatFileSize(item.file.size) }}
              </p>
            </div>

            <div class="flex items-center space-x-2">
              <select
                v-model="item.category"
                class="px-2.5 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
              >
                <option value="job_application">Job Application</option>
                <option value="background_check">Background Check</option>
                <option value="tax_mw507">Tax (MW507)</option>
                <option value="tax_i9">Tax (I-9)</option>
                <option value="tax_w4">Tax (W-4)</option>
                <option value="identification">Identification</option>
                <option value="certification">Certification</option>
                <option value="resume">Resume / CV</option>
                <option value="other">Other Document</option>
              </select>

              <button
                type="button"
                class="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40 rounded-lg transition cursor-pointer"
                title="Remove file"
                @click="newUploadFiles.splice(idx, 1)"
              >
                <Trash2 class="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="flex items-center justify-end space-x-3 w-full">
          <BaseButton
            variant="outline"
            size="md"
            text="Cancel"
            :disabled="isUploadingDocs"
            @click="closeUploadModal"
          />
          <BaseButton
            variant="success"
            size="md"
            :text="isUploadingDocs ? 'Uploading...' : (newUploadFiles.length > 0 ? `Upload ${newUploadFiles.length} Document${newUploadFiles.length === 1 ? '' : 's'}` : 'Upload Documents')"
            :loading="isUploadingDocs"
            :disabled="newUploadFiles.length === 0 || isUploadingDocs"
            :icon="UploadCloud"
            icon-position="left"
            @click="handleExecuteUploadDocs"
          />
        </div>
      </template>
    </BaseModal>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { BaseModal, BaseButton } from '@/components/common'
import PrintConfirmModal from '@/components/modals/PrintConfirmModal.vue'
import ViewApplicationDocumentModal from '@/components/modals/ViewApplicationDocumentModal.vue'
import type { ApplicationDetail, ApplicationDocument, ReferenceEntry } from '@/types/application'
import { applicationService } from '@/services/applicationService'
import { useToastService } from '@/services/toast'
import {
  FileText,
  User,
  ShieldCheck,
  Briefcase,
  Receipt,
  Paperclip,
  History,
  ClipboardCheck,
  Clock,
  GraduationCap,
  Car,
  CheckCircle2,
  Download,
  Printer,
  ExternalLink,
  Cpu,
  Users,
  UserPlus,
  UploadCloud,
  Eye,
  EyeOff,
  Trash2,
  Send,
  KeyRound,
  Check,
  X,
  MessageSquare,
  Phone,
  Mail,
  Edit3,
  Loader2,
} from 'lucide-vue-next'

interface Props {
  isVisible: boolean
  application?: ApplicationDetail | null
}

const props = defineProps<Props>()

const emit = defineEmits<{
  close: []
  review: [application: ApplicationDetail | null]
  'create-account': [application: ApplicationDetail]
  delete: [application: ApplicationDetail]
  'documents-updated': []
  'references-updated': [application: ApplicationDetail]
}>()

const toast = useToastService()

const activeTab = ref('job_application')
const isSsnRevealed = ref(false)
const isPrintSsnMasked = ref(true)
const isPrintConfirmVisible = ref(false)

// Access link action loading state
const isResendingLink = ref(false)
const isRevokingAccess = ref(false)

const handleResendAccessLink = async () => {
  if (!props.application?.application_id) return
  if (!confirm(`Generate and send a new secure edit link to ${props.application.email}?`)) {
    return
  }
  isResendingLink.value = true
  try {
    const res = await applicationService.resendAccessLink(props.application.application_id)
    toast.success(res.message || 'Access link sent successfully to applicant.')
    emit('documents-updated')
  } catch (err: any) {
    toast.error(err.response?.data?.message || err.message || 'Failed to resend access link.')
  } finally {
    isResendingLink.value = false
  }
}

const handleRevokeAccess = async () => {
  if (!props.application?.application_id) return
  if (!confirm(`Revoke active access tokens and edit sessions for Application #${props.application.application_id}? The applicant will no longer be able to log in or edit.`)) {
    return
  }
  isRevokingAccess.value = true
  try {
    const res = await applicationService.revokeAccess(props.application.application_id)
    toast.success(res.message || 'Applicant edit access has been revoked.')
    emit('documents-updated')
  } catch (err: any) {
    toast.error(err.response?.data?.message || err.message || 'Failed to revoke applicant access.')
  } finally {
    isRevokingAccess.value = false
  }
}

// Local documents state synced from props to avoid mutating props
const localDocuments = ref<ApplicationDocument[]>([])

watch(
  () => props.application?.documents,
  (docs) => {
    localDocuments.value = docs ? [...docs] : []
  },
  { immediate: true }
)

// Document preview state
const isPreviewModalVisible = ref(false)
const selectedDocForPreview = ref<ApplicationDocument | null>(null)

// Extra document upload state
const isUploadModalOpen = ref(false)
const isUploadingDocs = ref(false)
const isDragging = ref(false)
const extraFileInputRef = ref<HTMLInputElement | null>(null)
const newUploadFiles = ref<Array<{ file: File; category: string }>>([])

const isPaperRecord = computed(() => props.application?.record_type === 'paper')

const visibleTabs = computed(() => {
  if (isPaperRecord.value) {
    return [
      { id: 'job_application', label: 'Record Details', icon: FileText },
      { id: 'documents', label: 'Attachments', icon: Paperclip },
      { id: 'review_history', label: 'Review Notes', icon: History },
      { id: 'print_pdf', label: 'Print / PDF Preview', icon: Printer },
    ]
  }
  return [
    { id: 'job_application', label: 'Job Application', icon: Briefcase },
    { id: 'background_check', label: 'Background Check', icon: ShieldCheck },
    { id: 'tax_docs', label: 'Tax Documents', icon: Receipt },
    { id: 'documents', label: 'Attachments', icon: Paperclip },
    { id: 'review_history', label: 'Review Notes', icon: History },
    { id: 'print_pdf', label: 'Print / PDF Preview', icon: Printer },
  ]
})

const isPlaceholderEmail = (email?: string | null) => {
  if (!email) return true
  return email.startsWith('paper-record-') && email.endsWith('@jeducare.local')
}

const formatAddress = (app?: ApplicationDetail | null) => {
  if (!app) return ''
  const parts = [
    app.street_address,
    app.city,
    [app.state, app.zip_code].filter(Boolean).join(' ')
  ].filter(Boolean)
  return parts.join(', ')
}

watch(
  () => props.application,
  (newApp) => {
    if (newApp?.record_type === 'paper' && (activeTab.value === 'background_check' || activeTab.value === 'tax_docs')) {
      activeTab.value = 'job_application'
    }
  }
)

const jobApp = computed(() => props.application?.job_application_data)
const taxDocs = computed(() => props.application?.tax_doc_data)

// Local references state to allow reactive updates on verification actions
const localReferences = ref<ReferenceEntry[]>([])
const savingIndex = ref<number | null>(null)
const editingNoteIndex = ref<number | null>(null)
const editingNoteText = ref<string>('')

watch(
  () => props.application?.job_application_data?.references,
  (refs) => {
    localReferences.value = Array.isArray(refs) ? JSON.parse(JSON.stringify(refs)) : []
  },
  { immediate: true, deep: true }
)

const displayReferences = computed<ReferenceEntry[]>(() => {
  if (localReferences.value.length > 0) return localReferences.value
  return (props.application?.job_application_data?.references as ReferenceEntry[]) || []
})

const verifiedReferencesCount = computed(() => {
  return displayReferences.value.filter((r) => !!r.is_verified).length
})

const openEditNotes = (idx: number) => {
  editingNoteIndex.value = idx
  editingNoteText.value = displayReferences.value[idx]?.verification_notes || ''
}

const cancelEditNotes = () => {
  editingNoteIndex.value = null
  editingNoteText.value = ''
}

const saveNotesOnly = async (idx: number) => {
  if (!props.application?.application_id) return
  const currentRef = displayReferences.value[idx]
  if (!currentRef) return

  savingIndex.value = idx
  try {
    const res = await applicationService.updateReferenceVerification(
      props.application.application_id,
      {
        reference_index: idx,
        is_verified: !!currentRef.is_verified,
        verification_notes: editingNoteText.value.trim(),
      }
    )
    if (localReferences.value[idx]) {
      localReferences.value[idx].verification_notes = editingNoteText.value.trim()
    }
    toast.success('Reference notes saved.')
    editingNoteIndex.value = null
    editingNoteText.value = ''
    emit('references-updated', res.data)
    emit('documents-updated')
  } catch (err: any) {
    toast.error(err.response?.data?.message || err.message || 'Failed to save reference notes.')
  } finally {
    savingIndex.value = null
  }
}

const toggleReferenceVerification = async (idx: number) => {
  if (!props.application?.application_id) return
  const currentRef = displayReferences.value[idx]
  if (!currentRef) return

  const nextStatus = !currentRef.is_verified
  const notes = editingNoteIndex.value === idx ? editingNoteText.value.trim() : (currentRef.verification_notes || undefined)

  savingIndex.value = idx
  try {
    const res = await applicationService.updateReferenceVerification(
      props.application.application_id,
      {
        reference_index: idx,
        is_verified: nextStatus,
        verification_notes: notes,
      }
    )
    if (res.reference && localReferences.value[idx]) {
      localReferences.value[idx] = { ...localReferences.value[idx], ...res.reference }
    } else if (localReferences.value[idx]) {
      localReferences.value[idx].is_verified = nextStatus
      if (!nextStatus) {
        localReferences.value[idx].verified_at = null
        localReferences.value[idx].verified_by = null
      }
    }

    toast.success(res.message || (nextStatus ? 'Reference verified successfully.' : 'Reference verification cleared.'))
    if (editingNoteIndex.value === idx) {
      editingNoteIndex.value = null
      editingNoteText.value = ''
    }
    emit('references-updated', res.data)
    emit('documents-updated')
  } catch (err: any) {
    toast.error(err.response?.data?.message || err.message || 'Failed to update reference verification.')
  } finally {
    savingIndex.value = null
  }
}
const applicantSsn = computed(() => {
  return (
    jobApp.value?.social_security_number ||
    taxDocs.value?.mw507?.social_security_number ||
    taxDocs.value?.w4?.social_security_number ||
    taxDocs.value?.i9?.social_security_number ||
    ''
  )
})

const canCreateStaffAccount = (app?: ApplicationDetail | null) => {
  if (!app) return false
  if (app.fk_created_user_id || app.fk_linked_user_id) return false
  return app.application_status === 'approved' || app.record_type === 'paper'
}

const formatStatus = (status?: string) => {
  if (!status) return 'Pending'
  if (status.toLowerCase() === 'reviewed') return 'Under Review'
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()
}

const closeModal = () => {
  emit('close')
  activeTab.value = 'job_application'
  isSsnRevealed.value = false
}

const getInitials = (name?: string) => {
  if (!name) return 'AP'
  const parts = name.trim().split(' ')
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const maskSsn = (ssn?: string) => {
  if (!ssn) return 'N/A'
  const clean = ssn.replace(/\D/g, '')
  if (clean.length === 9) {
    return `***-**-${clean.substring(5)}`
  }
  return '***-**-****'
}

const formatFullSsn = (ssn?: string) => {
  if (!ssn) return ''
  const clean = ssn.replace(/\D/g, '')
  if (clean.length === 9) {
    return `${clean.substring(0, 3)}-${clean.substring(3, 5)}-${clean.substring(5)}`
  }
  if (ssn.includes('*') && clean.length >= 4) {
    return `123-45-${clean.slice(-4)}`
  }
  return ssn
}

const formatDate = (dateStr?: string | null) => {
  if (!dateStr) return 'N/A'
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

const formatFileSize = (bytes?: number) => {
  if (!bytes) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

const getStatusBadgeClass = (status?: string) => {
  switch (status) {
    case 'approved':
      return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-300'
    case 'rejected':
      return 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300'
    case 'reviewed':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
    case 'completed':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-950/40 dark:text-purple-300'
    case 'pending':
    default:
      return 'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
  }
}

const handleDownload = async (doc: ApplicationDocument) => {
  if (doc.document_id) {
    await applicationService.downloadDocument(doc.document_id, doc.file_name)
  }
}

const handleOpenPdfNewTab = async () => {
  if (!props.application?.application_id) return

  try {
    const blob = await applicationService.getApplicationPdfBlob(
      props.application.application_id,
      { mask_ssn: isPrintSsnMasked.value ? 1 : 0 }
    )
    const url = window.URL.createObjectURL(blob)
    window.open(url, '_blank')
  } catch (err) {
    console.error('Failed to open PDF in new tab:', err)
  }
}

const handleRequestPrint = () => {
  isPrintConfirmVisible.value = true
}

const handleConfirmPrint = async (options?: { includeRemarks?: boolean; includeSignatures?: boolean; maskSsn?: boolean }) => {
  isPrintConfirmVisible.value = false
  const maskSsnVal = options?.maskSsn !== false
  isPrintSsnMasked.value = maskSsnVal

  if (!props.application?.application_id) return

  const applicationId = props.application.application_id
  const sanitizedName = props.application.full_name?.replace(/[^a-zA-Z0-9_-]/g, '_') || 'Applicant'
  const fileName = `Jeducare_Application_${applicationId}_${sanitizedName}.pdf`
  const previewWindow = window.open('', '_blank')

  try {
    const blob = await applicationService.getApplicationPdfBlob(
      applicationId,
      { mask_ssn: maskSsnVal ? 1 : 0 }
    )
    const previewUrl = window.URL.createObjectURL(blob)

    if (previewWindow) {
      let printTriggered = false
      const printPreview = () => {
        if (printTriggered) return
        printTriggered = true
        previewWindow.focus()
        previewWindow.print()
      }
      previewWindow.onload = () => window.setTimeout(printPreview, 300)
      previewWindow.location.href = previewUrl
      window.setTimeout(printPreview, 1500)
    }

    const link = document.createElement('a')
    link.href = previewUrl
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.setTimeout(() => window.URL.revokeObjectURL(previewUrl), 60000)
  } catch (err: any) {
    previewWindow?.close()
    toast.error(err.response?.data?.message || err.message || 'Failed to print application PDF.')
  }
}

const handlePreview = (doc: ApplicationDocument) => {
  selectedDocForPreview.value = doc
  isPreviewModalVisible.value = true
}

const closeUploadModal = () => {
  isUploadModalOpen.value = false
  newUploadFiles.value = []
  isDragging.value = false
}

const triggerExtraFileInput = () => {
  extraFileInputRef.value?.click()
}

const handleExtraFileInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files?.length) {
    const maxBytes = 32 * 1024 * 1024
    Array.from(target.files).forEach((file) => {
      if (file.size > maxBytes) {
        toast.error(`File "${file.name}" exceeds the 32MB size limit.`)
        return
      }
      newUploadFiles.value.push({ file, category: 'other' })
    })
    target.value = ''
  }
}

const handleExtraFileDrop = (event: DragEvent) => {
  isDragging.value = false
  if (event.dataTransfer?.files?.length) {
    const maxBytes = 32 * 1024 * 1024
    Array.from(event.dataTransfer.files).forEach((file) => {
      if (file.size > maxBytes) {
        toast.error(`File "${file.name}" exceeds the 32MB size limit.`)
        return
      }
      newUploadFiles.value.push({ file, category: 'other' })
    })
  }
}

const handleExecuteUploadDocs = async () => {
  if (!props.application?.application_id || newUploadFiles.value.length === 0) return

  isUploadingDocs.value = true
  try {
    const formData = new FormData()
    newUploadFiles.value.forEach((item, index) => {
      formData.append(`documents[${index}]`, item.file)
      formData.append(`document_categories[${index}]`, item.category)
      formData.append(`document_names[${index}]`, item.file.name)
    })

    const response = await applicationService.uploadDocuments(props.application.application_id, formData)
    toast.success(response.message || 'Documents uploaded successfully.')

    if (response.data && Array.isArray(response.data)) {
      localDocuments.value = response.data
    }

    closeUploadModal()
    emit('documents-updated')
  } catch (err: any) {
    const msg = err.response?.data?.message || err.message || 'Failed to upload documents.'
    toast.error(msg)
  } finally {
    isUploadingDocs.value = false
  }
}
</script>

<style scoped>
@media print {
  body * {
    visibility: hidden;
  }
  #printable-application,
  #printable-application * {
    visibility: visible;
  }
  #printable-application {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    margin: 0;
    padding: 20px;
    border: none;
    box-shadow: none;
  }
}
</style>
